<template>
  <div class="object-replace">
    <section class="replacement-guide" aria-label="产品新增或替换操作指引">
      <header>
        <span><i class="el-icon-guide"></i></span>
        <div><strong>{{ operationHeading }}怎么操作？</strong><small>{{ operationGuideSummary }}</small></div>
      </header>
      <ol>
        <li v-for="(step, index) in guideSteps" :key="step.title">
          <b>{{ index + 1 }}</b>
          <span><strong>{{ step.title }}</strong><small>{{ step.note }}</small></span>
          <i :class="step.ready ? 'el-icon-circle-check ready' : 'el-icon-right'"></i>
        </li>
      </ol>
      <p><i class="el-icon-warning-outline"></i><span><strong>框选建议：</strong>{{ selectionEmptyText }}</span></p>
    </section>
    <div class="object-replace-layout">
      <aside class="controls">
        <section>
          <TitleStep
            number="01"
            title="空间场景"
            note="JPG / PNG / WebP，最大 10MB"
          />
          <label
            class="upload source"
            :class="{ filled: previewUrl, dragging: sourceDragging }"
            @dragenter.prevent="sourceDragging = true"
            @dragover.prevent="sourceDragging = true"
            @dragleave.prevent="sourceDragging = false"
            @drop.prevent="dropSource"
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              @change="pickSource"
            />
            <template v-if="previewUrl">
              <img :src="$ossImageUrl(previewUrl, 'preview')" alt="待处理空间图片" decoding="async" />
              <b><i class="el-icon-refresh"></i> 更换原图</b>
            </template>
            <template v-else>
              <i class="el-icon-picture-outline upload-icon"></i>
              <strong>上传待处理图片</strong><small>点击选择或拖到这里</small>
            </template>
          </label>
        </section>

        <section>
          <TitleStep
            number="02"
            :title="operationAreaTitle"
            :note="selectionStepNote"
          />
          <button
            class="selection-method"
            :class="{ ready: selection }"
            type="button"
            @click="showSelectionCanvas"
          >
            <i class="el-icon-crop"></i>
            <span
              ><strong>手动框选</strong
              ><small>{{
                selection ? selectedAreaLabel : "请在图片上拖出矩形区域"
              }}</small></span
            >
            <b>{{ selection ? "可重新框选" : "待框选" }}</b>
          </button>
          <div v-if="selection" class="selection-list">
            <button
              type="button"
              :disabled="generating"
              @click="clearSelection"
            >
              <i class="el-icon-check"></i><span>{{ operationAreaTitle }}已选定</span><b>重新框选</b>
            </button>
          </div>
          <div
            v-if="isLightingFixture && selection"
            class="lighting-size-lock"
            role="status"
            aria-live="polite"
          >
            <header>
              <span><i class="el-icon-lock"></i></span>
              <div>
                <strong>{{ lightingModeTitle }}</strong>
                <small>{{ lightingModeNote }}</small>
              </div>
            </header>
            <dl>
              <div><dt>宽度</dt><dd>{{ lightingTargetBounds.width }} px</dd></div>
              <div><dt>高度</dt><dd>{{ lightingTargetBounds.height }} px</dd></div>
              <div><dt>左上角</dt><dd>{{ lightingTargetBounds.left }}, {{ lightingTargetBounds.top }}</dd></div>
            </dl>
            <label>
              <span>安装定位方式</span>
              <select v-model="lightingAnchor" :disabled="generating">
                <option value="auto">自动识别</option>
                <option value="top">顶部安装点（吊灯/吸顶灯）</option>
                <option value="wall">墙面安装点（壁灯）</option>
                <option value="bottom">底部落点（台灯/落地灯）</option>
              </select>
            </label>
            <p><i class="el-icon-info"></i>{{ lightingModeHelp }}</p>
          </div>
          <p v-if="!selection" class="selection-empty">{{ selectionEmptyText }}</p>
        </section>

        <section>
          <TitleStep
            number="03"
            :title="replacementPreset.targetTitle"
            note="参考图或文字描述至少填写一项"
          />
          <label
            class="upload reference"
            :class="{
              filled: replacementPreviewUrl,
              dragging: referenceDragging,
            }"
            @dragenter.prevent="referenceDragging = true"
            @dragover.prevent="referenceDragging = true"
            @dragleave.prevent="referenceDragging = false"
            @drop.prevent="dropReference"
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              @change="pickReference"
            />
            <template v-if="replacementPreviewUrl">
              <img :src="$ossImageUrl(replacementPreviewUrl, 'preview')" :alt="`${replacementPreset.label}参考图`" decoding="async" />
              <b><i class="el-icon-refresh"></i> 更换参考图</b>
              <button
                class="remove-reference"
                title="移除参考图"
                @click.prevent="removeReference"
              >
                ×
              </button>
            </template>
            <template v-else>
              <i class="el-icon-camera upload-icon"></i>
              <strong>上传{{ replacementPreset.referenceLabel }}参考图 <em>选填</em></strong
              ><small>{{ replacementPreset.referenceExample }}</small>
            </template>
          </label>
          <button
            v-if="replacementPreviewUrl"
            type="button"
            class="reference-cutout-trigger"
            :class="{ completed: referencePrepared }"
            :disabled="generating || writingReplacementDescription || referencePreparing"
            :aria-busy="referencePreparing ? 'true' : 'false'"
            @click="prepareReferenceImage"
          >
            <i :class="referencePreparing ? 'el-icon-loading' : (referencePrepared ? 'el-icon-circle-check' : 'el-icon-scissors')"></i>
            <span>
              <strong>{{ referencePreparing ? referencePrepareStatus : (referencePrepared ? '已抠图并保留原素材' : '一键抠图·保留原图') }}</strong>
              <small>{{ referencePrepared ? '生成时将使用当前透明 PNG 的完整轮廓与比例' : '百度智能云抠图，不使用生成模型重画商品' }}</small>
            </span>
          </button>
          <p v-if="referencePrepareError" class="reference-cutout-error" role="alert">
            <i class="el-icon-warning-outline"></i>{{ referencePrepareError }}
          </p>
          <button
            type="button"
            class="material-library-trigger"
            :disabled="generating || furnitureMaterialLoading"
            @click="furnitureLibraryVisible = true"
          >
            <i :class="furnitureMaterialLoading ? 'el-icon-loading' : 'el-icon-collection'"></i>
            {{ furnitureMaterialLoading ? '正在添加素材' : (selectedFurnitureAsset ? `重新选择${materialLibraryLabel}素材` : `从${materialLibraryLabel}素材库选择`) }}
          </button>
          <p v-if="selectedFurnitureAsset" class="selected-material-name">
            <i class="el-icon-check"></i>
            已选择：{{ selectedFurnitureAsset.title }} · {{ selectedFurnitureAsset.subcategory }}
          </p>
          <p v-if="selectedFurnitureAsset" class="material-compression-note">
            <i class="el-icon-info"></i>
            生成时将自动使用高清压缩参考图，OSS 素材原图不会被修改。
          </p>
          <label class="description-label" for="replacement-description">
            <span>{{ operationMode === 'add' ? '新增描述' : '替换描述' }} <em>选填</em></span>
          </label>
          <textarea
            id="replacement-description"
            v-model.trim="replacementDescription"
            maxlength="300"
            :disabled="generating || writingReplacementDescription"
            :placeholder="replacementPreset.descriptionPlaceholder"
          ></textarea>
          <div class="description-tools">
            <div class="description-actions">
              <button
                type="button"
                :disabled="writingReplacementDescription || generating"
                @click.prevent="writeReplacementDescription"
              >
                <i
                  :class="writingReplacementDescription ? 'el-icon-loading' : 'el-icon-magic-stick'"
                ></i>
                {{ writingReplacementDescription ? "AI 正在写" : "AI 帮写" }}
              </button>
              <AiPromptOptimizer
                v-model="replacementDescription"
                class="description-optimizer"
                :disabled="generating || writingReplacementDescription"
                :max-length="300"
                :context="`${replacementPreset.label}的替换描述，目标是${replacementPreset.targetNoun}`"
              />
            </div>
            <small>{{ replacementDescription.length }}/300</small>
          </div>
          <div class="quick-prompts">
            <button
              v-for="item in quickPrompts"
              :key="item"
              :disabled="generating || writingReplacementDescription"
              @click="replacementDescription = item"
            >
              {{ item }}
            </button>
          </div>
        </section>
      </aside>

      <main class="stage">
        <div class="stage-toolbar">
          <div class="stage-heading">
            <span>04</span>
            <div>
              <strong>编辑与预览</strong
              ><small>在画布上指定位置并查看{{ operationActionText }}结果</small>
            </div>
          </div>
          <div class="stage-toolbar-actions">
            <div class="toolbar-group">
              <button
                :class="{ active: viewMode === 'mark' }"
                :disabled="!previewUrl"
                title="框选模式"
                @click="viewMode = 'mark'"
              >
                <i class="el-icon-crop"></i><span>框选</span>
              </button>
              <button
                :class="{ active: viewMode === 'result' }"
                :disabled="!resultUrl"
                :title="`查看${operationActionText}结果`"
                @click="viewMode = 'result'"
              >
                <i class="el-icon-view"></i><span>结果</span>
              </button>
            </div>
            <div class="toolbar-group compact">
              <button
                :disabled="!selection || generating"
                :title="`重新框选${operationAreaTitle}`"
                @click="clearSelection"
              >
                <i class="el-icon-refresh"></i>
              </button>
            </div>
          </div>
          <span v-if="previewUrl" class="image-meta"
            >{{ sourceWidth }} × {{ sourceHeight }} · {{ selectedRatio }}</span
          >
        </div>
        <div class="canvas-shell" :class="{ drawing: isDrawing }">
          <div v-if="!previewUrl" class="placeholder">
            <div class="canvas-empty-art">
              <span><i class="el-icon-picture-outline-round"></i></span>
              <i class="art-crop el-icon-crop"></i>
              <i class="art-spark">✦</i>
            </div>
            <strong>先上传一张空间图片</strong>
            <p>{{ replacementPreset.canvasEmpty }}</p>
            <em>请从左侧「空间场景」开始</em>
          </div>
          <template v-else-if="viewMode === 'mark' || !resultUrl">
            <canvas
              ref="replaceCanvas"
              :aria-label="replacementPreset.canvasAriaLabel"
              @pointerdown="startSelection"
              @pointermove="moveSelection"
              @pointerup="endSelection"
              @pointercancel="cancelSelection"
            ></canvas>
            <div v-if="!draftSelection" class="canvas-hint">
              <i class="el-icon-crop"></i>
              {{ selection ? "选区不合适可直接重新拖动，新框将替换当前选区" : canvasSelectionHint }}
            </div>
          </template>
          <div v-else class="result">
            <img :src="isLightingFixture ? resultUrl : $ossImageUrl(resultUrl, 'preview')" :alt="`${replacementPreset.label}结果`" decoding="async" /><span
              ><i class="el-icon-check"></i> {{ operationActionText }}完成</span
            >
          </div>
        </div>
        <div class="stage-footer">
          <div class="stage-tips">
            <span v-for="tip in replacementPreset.tips" :key="tip"><i class="el-icon-circle-check"></i> {{ tip }}</span>
          </div>
          <div v-if="resultUrl" class="stage-result-actions">
            <button
              class="local-edit-action"
              @click="openMaskedEditor(resultUrl, sourceWidth, sourceHeight)"
            >
              <i class="el-icon-edit-outline"></i> AI 局部编辑
            </button>
            <WorkflowTuningButton
              variant="button"
              :image-url="resultUrl"
              :prompt="finalPrompt"
              source-model="gpt-image-2-official"
              :module-name="replacementPreset.label"
            />
            <button @click="downloadResult">
              <i class="el-icon-download"></i> 下载结果
            </button>
          </div>
        </div>
        <div
          v-if="errorMessage"
          class="error-card"
          :class="{ processing: processingNotice }"
          :role="processingNotice ? 'status' : 'alert'"
          :aria-live="processingNotice ? 'polite' : 'assertive'"
        >
          <i :class="processingNotice ? 'el-icon-loading' : 'el-icon-warning-outline'"></i>
          <div>
            <strong>{{ errorMessage }}</strong
            ><small>{{ errorSuggestion }}</small>
          </div>
          <button
            v-if="(currentTaskIds.length || currentBatchId) && errorRetryable"
            :disabled="generating"
            @click="recoverPendingGeneration"
          >
            {{ currentTaskIds.length ? "继续查询" : "查询任务状态" }}
          </button>
        </div>
      </main>
    </div>

    <section class="generate-bar">
      <div class="generate-intro">
        <span><i class="el-icon-magic-stick"></i></span>
        <div>
          <strong>生成设置</strong
          ><small>确认操作方式、图片、区域和目标后开始处理</small>
        </div>
      </div>
      <div class="generation-status">
        <div class="readiness">
          <span :class="{ ready: previewUrl }"
            ><i
              :class="previewUrl ? 'el-icon-check' : 'el-icon-picture-outline'"
            ></i>
            原图已上传</span
          >
          <span :class="{ ready: selection }"
            ><i :class="selection ? 'el-icon-check' : 'el-icon-crop'"></i>
            已框选区域</span
          >
          <span :class="{ ready: hasReplacementTarget }"
            ><i
              :class="
                hasReplacementTarget ? 'el-icon-check' : 'el-icon-edit-outline'
              "
            ></i>
            已设置目标</span
          >
        </div>
        <ResolutionSelector
          v-model="selectedResolution"
          compact
          :disabled="generating || submissionPending"
        />
        <QualitySelector
          v-model="selectedQuality"
          compact
          :disabled="generating || submissionPending"
        />
        <div class="cost">
          <small>预计消耗</small><strong><i></i> {{ generationPoints }} 积分</strong>
        </div>
      </div>
      <button
        class="generate"
        :class="{ pending: submissionPending }"
        :disabled="generating || (!submissionPending && !canGenerate)"
        @click="handlePrimaryAction"
      >
        <span v-if="generating" class="spinner"></span
        ><i v-else :class="submissionPending ? 'el-icon-refresh' : 'el-icon-magic-stick'"></i>
        <span
          ><strong>{{ primaryActionText }}</strong>
          <small>{{
            generating
              ? `${progress}%`
              : submissionPending
                ? "任务已保存，无需重复提交"
                : operationMode === 'add'
                  ? "原有内容不被移除或替换"
                  : "保持未选区域完全不变"
          }}</small></span
        >
      </button>
      <div v-if="generating" class="generate-progress">
        <i :style="{ width: `${progress}%` }"></i>
      </div>
    </section>

    <details class="prompt-panel" open>
      <summary>
        <span class="prompt-panel-icon"><i class="el-icon-document"></i></span>
        <span class="prompt-panel-heading">
          <strong>生成图片提示词</strong>
          <small>系统预设约束、操作方式、原图尺寸、框选坐标和目标产品会自动合并</small>
        </span>
        <span class="prompt-panel-toggle"><i class="el-icon-arrow-down"></i></span>
      </summary>
      <div class="prompt-panel-body">
        <div class="prompt-panel-note">
          <span><i class="el-icon-info"></i> 以下是实际提交给图片模型的完整提示词，会随当前设置实时更新。</span>
          <button type="button" aria-label="复制生成图片提示词" @click="copyFinalPrompt">
            <i class="el-icon-document-copy"></i> 复制提示词
          </button>
        </div>
        <pre tabindex="0">{{ finalPrompt }}</pre>
      </div>
    </details>

    <section class="history">
      <header>
        <div>
          <span>GENERATION RECORDS</span>
          <h2>{{ replacementPreset.label }}记录</h2>
          <small>每次生成都会自动保存在这里，可随时查看或管理</small>
        </div>
        <button title="刷新记录" :disabled="historyLoading" @click="loadHistory">
          <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>
        </button>
      </header>
      <div v-if="!history.length" class="history-empty">
        <span><i :class="historyLoading ? 'el-icon-loading' : 'el-icon-time'"></i></span>
        <strong>{{ historyLoading ? '正在加载创作历史' : `暂无${replacementPreset.label}记录` }}</strong
        ><small>{{ historyLoading ? '正在获取历史记录，请稍候…' : '完成首次生成后，处理结果会自动出现在这里' }}</small>
      </div>
      <div v-else class="history-list">
        <article
          v-for="record in history"
          :key="record.id"
          :class="{
            active: record.id === currentBatchId,
            processing: record.status === 'generating',
            failed: record.status === 'failed',
          }"
        >
          <div class="history-meta">
            <div class="history-badges">
              <span :class="record.status">{{ statusLabel(record.status) }}</span>
              <span
                v-if="record.isLocalEdit"
                class="history-type-badge"
                title="这条记录由 AI 局部编辑生成"
              ><i class="el-icon-edit-outline"></i> AI 局部编辑</span>
            </div>
            <small><b>生成时间</b><time class="creation-date" :datetime="record.createdAt || undefined">{{ formatTime(record.createdAt) }}</time></small>
          </div>
          <div class="history-comparison">
            <button
              v-if="record.source"
              class="history-preview"
              title="查看替换前原图"
              @click="previewHistoryImage(record, 'source')"
            >
              <img :src="$ossImageUrl(record.source, 'thumbnail')" alt="替换前原图" loading="lazy" decoding="async" /><span
                class="history-image-label"
                >替换前</span
              ><i class="el-icon-zoom-in"></i>
            </button>
            <div v-else class="history-preview history-placeholder">
              <span class="history-image-label">替换前</span
              ><i class="el-icon-picture-outline"></i
              ><small>原图暂不可用</small>
            </div>
            <button
              v-if="record.result"
              class="history-preview result"
              title="查看替换后效果"
              @click="previewHistoryImage(record, 'result')"
            >
              <img :src="isLightingFixture ? record.result : $ossImageUrl(record.result, 'thumbnail')" alt="替换后效果图" loading="lazy" decoding="async" /><span
                class="history-image-label"
                >替换后</span
              ><i class="el-icon-zoom-in"></i>
            </button>
            <div
              v-else
              class="history-preview history-placeholder result"
              :class="{ processing: record.status === 'generating' }"
            >
              <span class="history-image-label">替换后</span
              ><i
                :class="
                  record.status === 'generating'
                    ? 'el-icon-loading'
                    : 'el-icon-warning-outline'
                "
              ></i
              ><small>{{
                record.status === "generating" ? "正在生成" : "生成失败"
              }}</small>
            </div>
          </div>
          <ImageDimensionsRow :items="[{ label: '原图尺寸', url: record.source, probe: true }, { label: '结果尺寸', url: record.result, width: record.resultWidth, height: record.resultHeight, probe: true }]" />
          <GenerationModelDetails :record="record" />
          <GenerationFailureDetails :record="record" />
          <div class="history-actions">
            <WorkflowTuningButton
              v-if="record.result"
              variant="button"
              :image-url="record.result"
              :prompt="record.prompt || finalPrompt"
              :source-model="record.model || ''"
              :module-name="replacementPreset.label"
            />
            <button
              v-if="record.result"
              class="local-edit-action"
              @click="openMaskedEditor(record.result, record.resultWidth, record.resultHeight)"
            >
              <i class="el-icon-edit-outline"></i> AI 局部编辑
            </button>
            <button
              :disabled="!record.source && !record.result"
              @click="
                previewHistoryImage(record, record.result ? 'result' : 'source')
              "
            >
              <i class="el-icon-view"></i> 查看对比
            </button>
            <button class="danger" @click="removeHistory(record)">
              <i class="el-icon-delete"></i>
            </button>
          </div>
        </article>
      </div>
      <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
    <FurnitureMaterialPicker
      :visible="furnitureLibraryVisible"
      :selected-id="selectedFurnitureAsset ? selectedFurnitureAsset.id : ''"
      :categories="materialCategories"
      :material-label="materialLibraryLabel"
      :action-label="replacementPreset.label"
      @close="furnitureLibraryVisible = false"
      @select="selectFurnitureMaterial"
    />
    <ReusableImageEditor
      :visible="maskedEdit.visible"
      :source-url="maskedEdit.sourceUrl"
      :submitting="maskedEdit.submitting"
      :error="maskedEdit.error"
      title="图片编辑"
      prompt-label="编辑提示词"
      prompt-placeholder="说明想把标记区域改成什么"
      :optimizer-context="`${replacementPreset.label}后的局部二次编辑：结合当前图片和可视标记区域优化修改提示词，未标记区域保持原样。`"
      @close="closeMaskedEditor"
      @save="submitMaskedEdit"
    />
  </div>
</template>

<script>
/* 框选层只用于页面交互；换灯具时仅把中心位置和大致尺寸交给模型，不提交矩形重绘区域。 */
import Vue from "vue";
import { apiRequest, downloadResource, postMultipartResource } from "@/services/api";
import ResolutionSelector from "@/components/ResolutionSelector.vue";
import QualitySelector from "@/components/QualitySelector.vue";
import ImageDimensionsRow from "@/components/ImageDimensionsRow.vue";
import AiPromptOptimizer from "@/components/AiPromptOptimizer.vue";
import ReusableImageEditor from "@/components/ReusableImageEditor.vue";
import { DEFAULT_GENERATION_QUALITY, MINIMUM_OUTPUT_RESOLUTION, generationPoints } from "@/utils/outputQuality";
import {
  selectionCoordinateText,
  selectionPixelBounds,
} from "@/utils/selectionGeometry";
import {
  mergePendingGenerations,
  removePendingGeneration,
  savePendingGeneration,
} from "@/utils/pendingGenerationRecords";

const RATIOS = [
  "1:1",
  "2:3",
  "3:2",
  "3:4",
  "4:3",
  "4:5",
  "5:4",
  "9:16",
  "16:9",
  "21:9",
];
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MATERIAL_CATEGORIES = Object.freeze({
  generic: [],
  curtain: ["curtain"],
  furniture: ["furniture"],
  background: ["texture"],
  floor: ["floor", "texture"],
  ceiling: ["texture"],
  lightingFixture: ["lighting"],
  doorWindow: ["doorWindow"],
  cabinet: ["cabinet"],
});
const REPLACEMENT_PRESETS = Object.freeze({
  generic: {
    label: "框选替换",
    areaTitle: "替换区域",
    targetTitle: "替换目标",
    targetNoun: "目标对象",
    referenceLabel: "替换目标",
    referenceExample: "例如想要替换成的家具、材质、装饰或其他对象参考图",
    selectionNote: "在右侧画布拖动，完整框住需要替换的内容",
    selectionEmpty: "完整框住需要替换的对象及其边缘，系统会保留选区外的空间结构与画面细节。",
    descriptionPlaceholder: "例如：将框选区域内的物体替换成浅色现代款，保持原位置、尺寸和透视",
    quickPrompts: ["替换成浅色现代款", "保持原位置与尺寸", "匹配现场材质与光影"],
    canvasEmpty: "上传后可在这里拖动框选任意需要替换的内容",
    canvasAriaLabel: "框选图片中需要替换的区域",
    canvasHint: "按住鼠标拖动，完整框住需要替换的内容",
    tips: ["完整覆盖替换对象", "保持框外画面不变", "自动匹配透视与光影"],
    geometryRule: "替换内容必须严格限制在选区内部，保持原对象所在位置、占位范围、透视关系、遮挡关系和相邻边界自然衔接。",
    promptInstruction: "移除选区内原有内容并替换成用户指定的目标对象；准确保持原空间结构、镜头透视、对象位置、占位尺度和遮挡关系，只在选区内部重建必要的材质、纹理、阴影与反射。"
  },
  curtain: {
    label: "换窗帘",
    areaTitle: "窗帘区域",
    targetTitle: "新窗帘方案",
    targetNoun: "窗帘",
    referenceLabel: "新窗帘",
    referenceExample: "例如窗帘实拍图或布料效果图",
    selectionNote: "在右侧画布拖动，完整框住原窗帘",
    selectionEmpty: "完整框住窗帘并保留窗框边缘，便于匹配褶皱、垂感与光线。",
    descriptionPlaceholder: "例如：换成米白色亚麻落地帘，双开褶皱，保留原窗轨",
    quickPrompts: ["米白亚麻落地帘", "奶咖色遮光帘", "白色柔纱帘"],
    canvasEmpty: "上传后可在这里拖动框选要更换的窗帘",
    canvasAriaLabel: "框选图片中需要更换的窗帘",
    canvasHint: "按住鼠标拖动，完整框住要更换的窗帘",
    tips: ["完整覆盖原窗帘", "保留窗框与窗轨", "自动匹配褶皱光影"],
    geometryRule: "新窗帘必须完整覆盖原窗帘区域，保持窗洞、窗框、窗轨和墙体边界不变。",
    promptInstruction: "移除原窗帘并换成指定窗帘，准确保留原窗洞、窗框、窗轨、帘盒和遮挡关系；新窗帘的安装高度、开合方向、褶皱密度、垂坠长度与室内光线必须自然。"
  },
  furniture: {
    label: "换家具",
    areaTitle: "家具区域",
    targetTitle: "新家具方案",
    targetNoun: "家具",
    referenceLabel: "新家具",
    referenceExample: "例如沙发、桌椅或床的产品图",
    selectionNote: "在右侧画布拖动，完整框住原家具",
    selectionEmpty: "框住整件家具并保留少量周围背景，有助于处理接地阴影与遮挡。",
    descriptionPlaceholder: "例如：换成浅米色单人休闲椅，原木扶手，尺寸与原椅接近",
    quickPrompts: ["浅米色休闲椅", "现代原木餐桌", "奶油风布艺沙发"],
    canvasEmpty: "上传后可在这里拖动框选要更换的家具",
    canvasAriaLabel: "框选图片中需要更换的家具",
    canvasHint: "按住鼠标拖动，完整框住要更换的家具",
    tips: ["框住完整家具", "参考图主体清晰", "自动匹配透视与光影"],
    geometryRule: "新家具的投影包围盒须贴合选区，并以原家具的底部接触点、视觉中心和透视方向为锚点。",
    promptInstruction: "完整移除原家具及其专属阴影、倒影，在原位置放入指定家具；以原家具实际占位为唯一尺度基准，保持合理接地、遮挡、透视和清晰度。"
  },
  background: {
    label: "换背景",
    areaTitle: "背景区域",
    targetTitle: "新背景方案",
    targetNoun: "背景",
    referenceLabel: "新背景",
    referenceExample: "例如墙面、景观或空间背景参考图",
    selectionNote: "在右侧画布拖动，框住需要更换的背景",
    selectionEmpty: "沿背景边界框选，避开需要保留的人物、家具和前景主体。",
    descriptionPlaceholder: "例如：换成暖白艺术涂料背景墙，细腻哑光质感",
    quickPrompts: ["暖白艺术涂料墙", "自然山景落地窗外景", "浅灰微水泥背景"],
    canvasEmpty: "上传后可在这里拖动框选要更换的背景区域",
    canvasAriaLabel: "框选图片中需要更换的背景区域",
    canvasHint: "按住鼠标拖动，框住需要更换的背景",
    tips: ["沿背景边界框选", "保留前景主体", "自动匹配空间光线"],
    geometryRule: "新背景应铺满选区并贴合原背景边界，前景人物、家具和装饰物必须完整保留。",
    promptInstruction: "只替换指定区域内的背景内容，保留前景主体及其轮廓、结构边线和遮挡关系；新背景应与原镜头透视、景深、色温和环境光一致。"
  },
  floor: {
    label: "换地面",
    areaTitle: "地面区域",
    targetTitle: "新地面方案",
    targetNoun: "地面材质",
    referenceLabel: "地面材质",
    referenceExample: "例如木地板、瓷砖或石材样板图",
    selectionNote: "在右侧画布拖动，框住需要更换的地面",
    selectionEmpty: "尽量覆盖完整可见地面，并保留墙地交界和家具落脚点。",
    descriptionPlaceholder: "例如：换成浅橡木人字拼地板，哑光自然纹理",
    quickPrompts: ["浅橡木人字拼", "米白哑光大砖", "浅灰微水泥地面"],
    canvasEmpty: "上传后可在这里拖动框选要更换的地面",
    canvasAriaLabel: "框选图片中需要更换的地面区域",
    canvasHint: "按住鼠标拖动，框住需要更换的地面",
    tips: ["覆盖完整可见地面", "保留墙地交界", "匹配透视与反射"],
    geometryRule: "新地面材质须铺满选区并遵循原地面的透视消失方向，墙地交界、踢脚线和家具落脚点不得移动。",
    promptInstruction: "仅替换可见地面的材质、颜色和铺贴方式；保持原地面高度、结构、透视网格、墙地交界、踢脚线、家具、投影和反射关系不变。"
  },
  ceiling: {
    label: "换吊顶",
    areaTitle: "吊顶区域",
    targetTitle: "新吊顶方案",
    targetNoun: "吊顶",
    referenceLabel: "新吊顶",
    referenceExample: "例如平顶、悬浮顶或造型吊顶参考图",
    selectionNote: "在右侧画布拖动，框住需要更换的吊顶",
    selectionEmpty: "覆盖完整可见顶面，并保留墙顶交界、灯位和风口位置。",
    descriptionPlaceholder: "例如：换成极简悬浮吊顶，四周暖色灯带，保留原灯位",
    quickPrompts: ["无主灯悬浮顶", "极简双眼皮吊顶", "平顶加线性灯"],
    canvasEmpty: "上传后可在这里拖动框选要更换的吊顶",
    canvasAriaLabel: "框选图片中需要更换的吊顶区域",
    canvasHint: "按住鼠标拖动，框住需要更换的吊顶",
    tips: ["覆盖完整可见顶面", "保留墙顶交界", "灯位与透视准确"],
    geometryRule: "新吊顶须贴合选区及原墙顶交界，保持层高、透视、梁柱、风口和未指定灯位不变。",
    promptInstruction: "仅更换选区内的吊顶造型与材质；保持原空间层高、墙顶边界、梁柱、风口、喷淋、未指定灯具的位置及整体镜头透视不变。"
  },
  lightingFixture: {
    label: "换灯具",
    areaTitle: "灯具区域",
    targetTitle: "新灯具方案",
    targetNoun: "灯具",
    referenceLabel: "新灯具",
    referenceExample: "例如吊灯、壁灯或落地灯产品图",
    selectionNote: "紧贴原灯具最外边缘框选，此框是新灯具的整体占位上限",
    selectionEmpty: "请沿原灯具最左、最右、最上、最下边缘紧贴框选。框选仅用于判断灯具中心位置和大致尺寸，不会作为矩形重绘区域提交。",
    descriptionPlaceholder: "例如：换成黑色极简线性吊灯，长度与餐桌协调",
    quickPrompts: ["黑色线性吊灯", "奶油风玻璃吊灯", "黄铜球形壁灯"],
    canvasEmpty: "上传后可在这里拖动框选要更换的灯具",
    canvasAriaLabel: "框选图片中需要更换的灯具",
    canvasHint: "紧贴旧灯具四周拖出占位框，系统会保持参考灯具自身比例",
    tips: ["整体占位上限锁定", "参考灯具比例不变", "周围照明保持自然"],
    geometryRule: "新灯具必须完整放入原灯具占位框并保持参考产品自身宽高比，不允许越界、拉伸、压扁或移位。",
    promptInstruction: "完整移除原灯具及其专属光斑、投影和反射，再在原安装位置换成指定灯具；参考图只提取灯具主体，必须忽略参考图的白底、场景背景、四周留白和展示光效。保持安装点、线路、悬挂高度和空间结构不变；照射方向必须由新灯具真实的专用发光面、灯罩开口、光学结构和实际光路重新决定，不得继承旧灯具或参考产品图的配光。"
  },
  doorWindow: {
    label: "换门窗",
    areaTitle: "门窗区域",
    targetTitle: "新门窗方案",
    targetNoun: "门窗",
    referenceLabel: "新门窗",
    referenceExample: "例如室内门、移门或窗型参考图",
    selectionNote: "在右侧画布拖动，完整框住原门或窗",
    selectionEmpty: "完整框住门窗洞口，并保留与墙体衔接的边框位置。",
    descriptionPlaceholder: "例如：换成奶油白窄边玻璃移门，保留原洞口尺寸",
    quickPrompts: ["奶油白窄边移门", "深色极简木门", "黑色窄框落地窗"],
    canvasEmpty: "上传后可在这里拖动框选要更换的门或窗",
    canvasAriaLabel: "框选图片中需要更换的门窗",
    canvasHint: "按住鼠标拖动，完整框住要更换的门或窗",
    tips: ["框住完整门窗洞口", "保持墙体结构", "匹配开启方向与透视"],
    geometryRule: "新门窗必须严格沿用原洞口四边、墙体厚度、离地位置和透视方向，不得扩大或移动洞口。",
    promptInstruction: "在原洞口内更换指定门窗，保持洞口尺寸、墙体结构、门槛、窗台和相邻收口不变；准确匹配框扇比例、开启逻辑、玻璃反射和室内外光线。"
  },
  cabinet: {
    label: "换柜子",
    areaTitle: "柜体区域",
    targetTitle: "新柜体方案",
    targetNoun: "柜体",
    referenceLabel: "新柜体",
    referenceExample: "例如衣柜、橱柜或玄关柜参考图",
    selectionNote: "在右侧画布拖动，完整框住原柜体",
    selectionEmpty: "完整框住柜体及收口，保留相邻墙面、地面和台面边界。",
    descriptionPlaceholder: "例如：换成奶油白通顶柜，平板柜门，无拉手设计",
    quickPrompts: ["奶油白通顶柜", "胡桃木悬空柜", "浅灰无拉手橱柜"],
    canvasEmpty: "上传后可在这里拖动框选要更换的柜子",
    canvasAriaLabel: "框选图片中需要更换的柜体",
    canvasHint: "按住鼠标拖动，完整框住要更换的柜子",
    tips: ["框住完整柜体", "保留墙地收口", "匹配进深与透视"],
    geometryRule: "新柜体须沿用原柜体的占位、进深、落地线、靠墙面和视觉中心，并准确衔接相邻墙地面。",
    promptInstruction: "完整移除原柜体并在原位置换成指定柜体；保持墙体、地面、台面、插座和相邻物体不变，准确匹配柜体尺寸、进深、分缝、收口、遮挡与投影。"
  }
});
const TitleStep = Vue.extend({
  functional: true,
  props: { number: String, title: String, note: String },
  render(h, context) {
    return h("div", { class: "step-title" }, [
      h("span", context.props.number),
      h("div", [
        h("strong", context.props.title),
        h("small", context.props.note),
      ]),
    ]);
  },
});

export default {
  name: "ObjectReplacement",
  components: {
    FurnitureMaterialPicker: () => import("@/components/FurnitureMaterialPicker.vue"),
    ResolutionSelector,
    QualitySelector,
    ImageDimensionsRow,
    AiPromptOptimizer,
    ReusableImageEditor,
    TitleStep,
  },
  props: {
    replacementType: {
      type: String,
      default: "generic",
    },
  },
  data() {
    return {
      sourceFile: null,
      previewUrl: "",
      sourceImage: null,
      sourceWidth: 0,
      sourceHeight: 0,
      selectedRatio: "16:9",
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      selection: null,
      draftSelection: null,
      drawingStart: null,
      drawingPointerId: null,
      isDrawing: false,
      sourceDragging: false,
      replacementFile: null,
      replacementAssetReference: null,
      replacementPreviewUrl: "",
      selectedFurnitureAsset: null,
      furnitureLibraryVisible: false,
      furnitureMaterialLoading: false,
      referenceDragging: false,
      referencePreparing: false,
      referencePrepared: false,
      referencePrepareStatus: "",
      referencePrepareError: "",
      replacementDescription: "",
      operationMode: "replace",
      lightingAnchor: "auto",
      writingReplacementDescription: false,
      viewMode: "mark",
      resultUrl: "",
      maskedEdit: {
        visible: false,
        sourceUrl: "",
        candidateUrl: "",
        width: 0,
        height: 0,
        submitting: false,
        error: "",
        iteration: 0,
      },
      generating: false,
      generationText: "正在准备",
      progress: 0,
      apiConfigured: false,
      currentBatchId: "",
      currentTaskIds: [],
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyTotal: 0,
      errorMessage: "",
      errorSuggestion: "",
      errorRetryable: false,
      requestControllers: [],
      pollingStopped: false,
      pendingRecordTimer: null,
      historyLoadErrorNotified: false,
    };
  },
  computed: {
    replacementPreset() {
      return REPLACEMENT_PRESETS[this.detectedReplacementType] || REPLACEMENT_PRESETS.generic;
    },
    replacementIntentText() {
      const asset = this.selectedFurnitureAsset || {};
      return [
        asset.title,
        asset.name,
        asset.category,
        asset.subcategory,
        asset.type,
        this.replacementDescription,
      ].filter(Boolean).join(" ");
    },
    detectedReplacementType() {
      if (this.replacementType !== "generic") return this.replacementType;
      const text = this.replacementIntentText;
      if (/吊灯|吸顶灯|壁灯|落地灯|台灯|灯具|灯罩|灯头|吊线|吸顶盘/.test(text)) return "lightingFixture";
      if (/窗帘|纱帘|遮光帘|百叶帘/.test(text)) return "curtain";
      if (/门窗|房门|移门|推拉门|窗户|窗框/.test(text)) return "doorWindow";
      if (/柜子|柜体|衣柜|橱柜|玄关柜|书柜/.test(text)) return "cabinet";
      if (/吊顶|天花|顶面/.test(text)) return "ceiling";
      if (/地板|地砖|地面/.test(text)) return "floor";
      if (/沙发|椅子|桌子|茶几|床|家具/.test(text)) return "furniture";
      return "generic";
    },
    isLightingFixture() {
      return this.detectedReplacementType === "lightingFixture";
    },
    generationPoints() {
      return generationPoints(this.selectedQuality);
    },
    lightingFixtureIntentText() {
      const assetText = this.selectedFurnitureAsset
        ? `${this.selectedFurnitureAsset.title || ""} ${this.selectedFurnitureAsset.subcategory || ""}`
        : "";
      return `${assetText} ${this.replacementDescription || ""}`.trim();
    },
    isLightingFixtureAddition() {
      return this.isLightingFixture && this.operationMode === "add";
    },
    isAddition() {
      return this.operationMode === "add";
    },
    operationActionText() {
      return this.isAddition ? "新增" : "替换";
    },
    operationHeading() {
      return this.isAddition ? `新增${this.replacementPreset.targetNoun}` : this.replacementPreset.label;
    },
    operationAreaTitle() {
      return this.isAddition ? "产品放置区域" : this.replacementPreset.areaTitle;
    },
    operationGuideSummary() {
      return this.isAddition
        ? "框选产品要出现的位置；系统保留原图，只增加新产品及必要的接触光影"
        : "完整框住要替换的旧内容；框外空间结构、家具和光影会被锁定保护";
    },
    selectionStepNote() {
      if (this.isAddition) return `框住新${this.replacementPreset.targetNoun}要放置的位置和最大占位范围`;
      if (!this.isLightingFixture) return this.replacementPreset.selectionNote;
      return this.replacementPreset.selectionNote;
    },
    selectionEmptyText() {
      if (this.isAddition) return `框住新${this.replacementPreset.targetNoun}最终要出现的位置，框内不需要存在旧产品；已有内容不会被删除或替换。`;
      if (!this.isLightingFixture) return this.replacementPreset.selectionEmpty;
      return this.replacementPreset.selectionEmpty;
    },
    canvasSelectionHint() {
      if (this.isAddition) return `拖动框住新${this.replacementPreset.targetNoun}要放置的位置和最大范围`;
      if (!this.isLightingFixture) return this.replacementPreset.canvasHint;
      return this.replacementPreset.canvasHint;
    },
    lightingModeTitle() {
      if (this.isLightingFixtureAddition) return "新增灯具区域已选定";
      return "旧灯具占位已选定";
    },
    lightingModeNote() {
      if (this.isLightingFixtureAddition) return "可重新框选；当前范围是最大边界，灯具按空间真实尺度生成";
      return "可重新框选；新灯具不允许超出当前尺寸框";
    },
    lightingModeHelp() {
      if (this.isLightingFixtureAddition) return "新增时蓝框是最大放置范围，不会强制把灯具撑满；建议选择准确落点和合理范围。";
      return "蓝框只用于识别旧灯具及其大致尺寸，系统会保持参考灯具原始比例。";
    },
    lightingFixtureScalePrompt() {
      const text = this.lightingFixtureIntentText;
      if (/落地灯|地灯/.test(text)) {
        return "落地灯应采用真实住宅尺度：整体视觉高度通常约为同处可见层高的 40%—60%，灯体最大宽度通常不超过自身高度的 15%（弧形落地灯除外）；以附近桌面、椅背、柜格和踢脚线作为比例尺，底座必须真实落地，不得顶到天花或形成从地面贯穿到顶面的发光立柱。";
      }
      if (/台灯/.test(text)) {
        return "台灯应采用真实桌面灯具尺度：整体高度通常约为承托台面离地高度的 45%—80%，底座完整落在台面内，不得被放大成落地灯或遮挡相邻家具。";
      }
      if (/壁灯/.test(text)) {
        return "壁灯应采用真实墙面灯具尺度：灯体只占所在墙段的小部分，并与墙板分缝、柜格、开关和附近家具形成合理比例，不得放大成大面积发光装置。";
      }
      if (/吊灯|吸顶灯|顶灯/.test(text)) {
        return "顶装灯具应根据层高、桌面或家具尺度推断真实直径与垂吊长度，保留合理净空，不得因选区较大而把灯罩或发光体撑满整个选区。";
      }
      return "先依据附近家具、门洞、柜格、踢脚线和可见层高建立真实空间比例，再决定灯具尺寸；灯具应是空间中的正常产品尺度，不得自动填满选区或成为夸张的主体装置。";
    },
    lightingTargetBounds() {
      if (!this.selection || !this.sourceWidth || !this.sourceHeight) {
        return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
      }
      return selectionPixelBounds(this.selection, this.sourceWidth, this.sourceHeight);
    },
    effectiveLightingAnchor() {
      if (this.lightingAnchor !== "auto") return this.lightingAnchor;
      const assetText = this.selectedFurnitureAsset
        ? `${this.selectedFurnitureAsset.title || ""} ${this.selectedFurnitureAsset.subcategory || ""}`
        : "";
      const description = `${assetText} ${this.replacementDescription}`;
      if (/壁灯/.test(description)) return "wall";
      if (/台灯|落地灯|地灯/.test(description)) return "bottom";
      if (/吊灯|吸顶灯|顶灯/.test(description)) return "top";
      return "auto";
    },
    lightingAnchorPrompt() {
      const placementMode = this.isLightingFixtureAddition;
      const labels = placementMode
        ? {
            top: "根据参考灯具的顶部连接结构，在定位点附近寻找真实可用的顶面安装点，灯体向下自然展开",
            wall: "根据参考灯具的墙装底座，在定位点附近的真实墙面安装并贴合透视",
            bottom: "在定位点附近寻找可见台面或地面作为承托面，使参考灯具底部真实接触承托面，不得悬空或穿透",
            auto: "先根据第二张参考图识别新灯具是吊装、墙装、落地还是台面放置，再依据定位点寻找对应的安装面或承托面；不得臆造吊线、底座或安装方式",
          }
        : {
            top: "沿用旧灯具原有的顶部安装点，安装点不得移动",
            wall: "以旧灯具的墙面安装底座中心为锚点，底座中心的像素位置绝对不得移动",
            bottom: "沿用旧灯具原有的底部落点，落点不得移动",
            auto: "先识别旧灯具是吊装、墙装还是落地或台面放置，然后分别锁定顶部安装点、墙面底座中心或底部落点",
          };
      return labels[this.effectiveLightingAnchor] || labels.auto;
    },
    quickPrompts() {
      return this.replacementPreset.quickPrompts;
    },
    materialCategories() {
      return MATERIAL_CATEGORIES[this.replacementType] || [];
    },
    materialLibraryLabel() {
      const labels = {
        generic: "综合",
        curtain: "窗帘",
        furniture: "家具",
        background: "背景材质",
        floor: "地面",
        ceiling: "吊顶材质",
        lightingFixture: "灯具",
        doorWindow: "门窗",
        cabinet: "柜子",
      };
      return labels[this.replacementType] || "综合";
    },
    selectedAssetPrompt() {
      const asset = this.selectedFurnitureAsset;
      if (!asset) return "";
      const identity = [asset.subcategory, asset.collection, asset.title]
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index)
        .join("，");
      return identity
        ? `素材库产品身份：${identity}。这是产品类别和材质判断的明确依据；必须把参考主体识别为一件完整产品，不得误判成其他品类，也不得只提取其中一个局部组件。`
        : "";
    },
    hasReplacementTarget() {
      return Boolean(
        this.replacementFile || this.replacementAssetReference || this.replacementDescription.trim()
      );
    },
    guideSteps() {
      return [
        { title: "上传空间原图", note: "使用画面清晰、无遮挡的 JPG / PNG / WebP", ready: Boolean(this.previewUrl) },
        { title: this.isAddition ? `指定${this.replacementPreset.targetNoun}位置` : `完整框住${this.replacementPreset.targetNoun}`, note: this.selectionStepNote, ready: Boolean(this.selection) },
        { title: `设置新${this.replacementPreset.targetNoun}`, note: "上传产品图或填写文字描述，至少完成一项", ready: this.hasReplacementTarget },
        { title: "生成并核对结果", note: "重点检查框外画面、尺寸、透视和边缘衔接", ready: Boolean(this.resultUrl) },
      ];
    },
    canGenerate() {
      return Boolean(
        this.sourceFile &&
          this.sourceImage &&
          this.selection &&
          this.hasReplacementTarget &&
          !this.writingReplacementDescription &&
          this.apiConfigured
      );
    },
    submissionPending() {
      if (!this.currentBatchId || this.resultUrl) return false;
      if (this.currentTaskIds.length) return true;
      const record = this.history.find((item) => item.id === this.currentBatchId);
      return Boolean(record && record.status === "generating");
    },
    processingNotice() {
      if (!this.errorMessage || !this.errorRetryable) return false;
      if (this.generating || this.submissionPending || this.currentTaskIds.length) return true;
      const record = this.history.find((item) => item.id === this.currentBatchId);
      return Boolean(record && record.status === "generating");
    },
    primaryActionText() {
      if (this.generating) return this.generationText;
      if (this.submissionPending) return "继续查询";
      return this.resultUrl ? `继续${this.operationActionText}` : `立即${this.operationActionText}`;
    },
    selectedAreaLabel() {
      if (this.isLightingFixture && this.selection) {
        return `${this.lightingTargetBounds.width} × ${this.lightingTargetBounds.height} px 已选定`;
      }
      return this.selection
        ? `覆盖画面的 ${
            Math.round(this.selection.width * this.selection.height * 1000) / 10
          }%`
        : "";
    },
    selectionGeometryPrompt() {
      if (!this.selection) return "";
      if (this.isLightingFixture) {
        const box = this.lightingTargetBounds;
        const centerX = Math.round((this.selection.x + this.selection.width / 2) * 1000) / 10;
        const centerY = Math.round((this.selection.y + this.selection.height / 2) * 1000) / 10;
        const subject = this.isLightingFixtureAddition ? "新灯具预期位置" : "旧灯具视觉中心";
        return `${subject}约在原图横向 ${centerX}%、纵向 ${centerY}%；期望灯具的整体视觉尺寸约为 ${box.width}×${box.height}px。以上数值只用于识别灯具对象、安装位置和大致尺度，不是矩形编辑边界、蒙版、裁切框或重绘范围，输出中不得出现任何矩形分区痕迹。${this.lightingAnchorPrompt}。`;
      }
      if (this.isAddition) {
        return `${selectionCoordinateText(this.selection, "新产品预期放置范围")}。该坐标框是产品主体的最大占位范围和位置提示，不是替换区域、擦除区域、蒙版或矩形重绘边界；新产品主体必须自然放入该范围并保持自身比例。`;
      }
      return `${selectionCoordinateText(this.selection, this.replacementPreset.areaTitle)}。${this.replacementPreset.geometryRule}`;
    },
    sourceDimensionPrompt() {
      if (!this.sourceWidth || !this.sourceHeight) {
        return "最终输出必须保持与第一张原图完全相同的像素宽高、长宽比和横竖方向，不得裁剪、扩图、缩放或拉伸。";
      }
      return `第一张原图的实际像素尺寸为 ${this.sourceWidth}×${this.sourceHeight}；最终输出必须仍为 ${this.sourceWidth}×${this.sourceHeight}，像素宽高、长宽比和横竖方向都不得改变，不得裁剪、扩图、缩放或拉伸。`;
    },
    lightingGenerationPrompt() {
      if (!this.isLightingFixture || !this.selection) return "";
      const box = this.lightingTargetBounds;
      const locator = this.selectionGeometryPrompt;
      const reference = this.replacementFile || this.replacementAssetReference
        ? (this.referencePrepared
          ? "第二张图片是已抠图的透明高清新灯具素材，必须完整保留其精确外轮廓、原始宽高比、灯罩、底座、支架、连接结构、材质和颜色；只允许等比缩放和匹配现场光影，不得重画成类似款、拉伸、压扁或漏掉任何部件。"
          : "第二张图片是新灯具参考图，只参考完整灯具的造型、材质和颜色，不复制参考图背景、视角、光线或场景构图。")
        : "";
      const target = this.replacementDescription.trim()
        ? `${this.isAddition ? "新增" : "更换"}灯具目标描述：${this.replacementDescription.trim()}。`
        : `${this.isAddition ? "新增" : "更换"}灯具目标以第二张参考图中的灯具为准。`;
      let actionRule;
      let sizeRule;
      if (this.isLightingFixtureAddition) {
        actionRule = "在指定位置自然新增灯具，保留原有空间内容，并准确匹配安装或落地关系。";
        sizeRule = `【尺寸协调为最高优先级】参考约 ${box.width}×${box.height}px 的期望视觉尺寸，并结合附近门洞、家具和层高确定真实尺度；保持参考灯具自身宽高比做统一等比缩放，完整保留全部部件，不得拉伸、压扁、裁切或因参考图留白改变判断。`;
      } else {
        actionRule = "完整移除原灯具并在原安装位置换成指定灯具；保持安装点、线路、悬挂高度和空间结构不变。";
        sizeRule = `【尺寸协调为最高优先级】旧灯具整体视觉尺寸约为 ${box.width}×${box.height}px。新灯具必须保持参考产品自身宽高比做统一等比缩放，视觉中心和安装点不变，整体占位尽可能接近旧灯具；参考产品比例与旧灯不同时，优先保持参考产品不变形，并使较长的视觉主尺寸约为旧灯对应主尺寸的 90%—105%，另一尺寸按自身比例自然确定。不得拉伸、压扁、裁切，也不得把产品图白底或四周留白计入灯具尺寸。`;
      }
      const existingLightRule = this.isLightingFixtureAddition
        ? "不得移除或替换原图中的任何已有灯具及其光效；只为新增灯具补充符合其真实光学结构的必要照明。"
        : "先移除原灯具及其专属光斑、投影和反射，同时保留自然光、环境光及其他灯具的照明。";
      return `基于第一张原始空间图生成一张完整、连续、无接缝的最终场景图，只${this.operationActionText}指定灯具对象，绝不能生成或粘贴矩形局部补丁。${this.sourceDimensionPrompt}${locator}${reference}${target}${sizeRule}${actionRule}新灯具须与原空间的落点、遮挡、透视和清晰度自然一致。${existingLightRule}只按新灯具真实的专用发光面、灯罩开口、光学结构和实际光路重新计算新增配光，不得继承参考产品图的展示光效。【向上照明严格判定】只有产品明确设计了朝上的独立发光面、透镜、导光板或专用反光结构，才允许形成向上照明；普通外露灯泡、镂空灯架、透明装饰件、金属枝叶或灯泡上方无遮挡，都不能视为上照结构。没有专用上照结构时，灯具上方天花只保留原场景环境光，不得新增提亮、光圈、光束或放射投影；即使物理上存在少量溢光，也只能是几乎不可察觉、均匀无图案的柔和环境反射。灯具不发光时不新增光效；发光时仅在真实主要出光方向的受光表面形成克制柔和的照明，禁止把灯体装饰轮廓投射成重复、对称或放射状阴影。整张图的顶面、墙面、线条、材质纹理、曝光和色温必须连续一致，灯具周围不得出现任何矩形色块、直线接缝、方形明暗差、局部画布或蒙版边界。不得生成色框、遮罩、文字、水印、标注或拼图。`;
    },
    finalPrompt() {
      if (this.isLightingFixture) return this.lightingGenerationPrompt;
      const targetLabel = this.isAddition ? `新增${this.replacementPreset.targetNoun}` : this.replacementPreset.label;
      const target = this.replacementDescription.trim()
        ? `${targetLabel}目标描述：${this.replacementDescription.trim()}。`
        : `${targetLabel}目标以第二张参考图中的${this.replacementPreset.targetNoun}为准。`;
      const reference = this.replacementFile || this.replacementAssetReference
        ? (this.referencePrepared
          ? `第二张图片是已抠图的透明高清${this.replacementPreset.targetNoun}素材，必须完整保留其精确外轮廓、原始宽高比、全部组成部件、连接结构、材质和颜色；只允许等比缩放与现场透视光影融合，不得重画成类似款、拉伸、压扁、裁切或漏掉部件。`
          : `第二张图片是${this.replacementPreset.referenceLabel}参考图，只参考${this.replacementPreset.targetNoun}的造型、材质和颜色，不复制参考图背景、视角、光线或场景构图。`)
        : "";
      if (this.isAddition) {
        return `执行室内图片局部新增产品。第一张且唯一的场景图片是必须保持的干净原始空间图，页面框线不会作为图片提交；只依据下方精确坐标确定新产品的放置位置和最大占位。${this.sourceDimensionPrompt}${this.selectionGeometryPrompt}${reference}${target}【只新增，不替换】在指定位置自然加入一件完整的${this.replacementPreset.targetNoun}，绝对禁止删除、擦除、替换、移动、缩放、变形或重新设计原图中的任何已有物体、结构、材质和装饰。选区内原有画面也必须保留，只有被新产品实体自然遮挡的像素，以及为接地或安装所必需的极小范围接触阴影、反射和遮挡关系可以改变。不得把框内背景整体重绘成新内容，不得为了腾出位置而移除任何物体。新产品主体应完整落在指定范围内，保持参考产品自身比例，并与原空间的尺度、承托面或安装面、落点、遮挡、镜头透视、清晰度和光影自然一致。【原图完全锁定】除新产品及其物理上必要的接触光影外，整张原图必须直接继承，禁止全图重绘；原图与输出图须按相同像素尺寸直接重合叠加。不得改变镜头、构图、房间结构、曝光、色温或未涉及区域，不得生成色框、遮罩、编号、文字、水印、标注或对比拼图。`;
      }
      return `执行室内图片局部${this.replacementPreset.label}。第一张且唯一的场景图片是必须保持的干净原始空间图，页面框线不会作为图片提交；只依据下方精确坐标定位处理区域。${this.sourceDimensionPrompt}${this.selectionGeometryPrompt}${reference}${target}${this.replacementPreset.promptInstruction}处理结果不得越出指定区域，不得拉伸变形，必须与原空间的尺度、落地点、遮挡关系、镜头透视、清晰度和光影自然一致。【框外完全锁定】只允许改变上述坐标区域内部，选区以外的画面必须直接继承第一张原图，禁止全图重绘；边缘融合只能向选区内部进行。【前后对比校验】原图与输出图必须能按相同像素尺寸直接重合叠加，除处理区域外不得出现位置偏移、边缘闪动、明暗变化、色差或纹理差异。不得改变原图镜头、构图、房间结构和未选区域，不得生成色框、遮罩、编号、文字、水印、标注或对比拼图。`;
    },
    viewerItems() {
      return this.history.reduce((items, record) => {
        if (record.source)
          items.push({
            src: record.source,
            title: `${this.replacementPreset.label} · 替换前`,
            key: `${record.id}-source`,
          });
        if (record.result)
          items.push({
            src: record.result,
            title: `${this.replacementPreset.label} · 替换后`,
            key: `${record.id}-result`,
          });
        return items;
      }, []);
    },
  },
  mounted() {
    window.addEventListener("resize", this.drawCanvas);
    this.loadConfiguration();
    this.loadHistory();
  },
  watch: {
    replacementType(next, previous) {
      if (next !== previous) this.switchReplacementMode();
    },
  },
  beforeDestroy() {
    this.pollingStopped = true;
    if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer);
    window.removeEventListener("resize", this.drawCanvas);
    this.requestControllers.forEach((controller) => controller.abort());
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    if (this.replacementPreviewUrl)
      URL.revokeObjectURL(this.replacementPreviewUrl);
  },
  methods: {
    showSelectionCanvas() {
      this.viewMode = "mark";
      this.$nextTick(this.drawCanvas);
    },
    setOperationMode(mode) {
      if (mode !== "add" && mode !== "replace") return;
      if (this.operationMode === mode) return;
      this.operationMode = mode;
      this.selection = null;
      this.draftSelection = null;
      this.resultUrl = "";
      this.viewMode = "mark";
      this.$nextTick(this.drawCanvas);
      this.message(mode === "add" ? "已切换为新增产品，请框选产品放置位置" : "已切换为替换，请完整框住原有内容", "info");
    },
    switchReplacementMode() {
      if (this.replacementPreviewUrl && !this.replacementAssetReference) URL.revokeObjectURL(this.replacementPreviewUrl);
      Object.assign(this, {
        replacementFile: null,
        replacementAssetReference: null,
        replacementPreviewUrl: "",
        selectedFurnitureAsset: null,
        furnitureLibraryVisible: false,
        replacementDescription: "",
        operationMode: "replace",
        lightingAnchor: "auto",
        selection: null,
        draftSelection: null,
        drawingStart: null,
        resultUrl: "",
        maskedEdit: {
          visible: false,
          sourceUrl: "",
          candidateUrl: "",
          width: 0,
          height: 0,
          submitting: false,
          error: "",
          iteration: 0,
        },
        viewMode: "mark",
        currentBatchId: "",
        currentTaskIds: [],
        errorMessage: "",
        errorSuggestion: "",
        errorRetryable: false,
        history: [],
        historyPage: 1,
        historyTotal: 0,
      });
      if (this.pendingRecordTimer) {
        window.clearTimeout(this.pendingRecordTimer);
        this.pendingRecordTimer = null;
      }
      this.$nextTick(() => {
        this.drawCanvas();
        this.loadHistory();
      });
    },
    message(text, type = "info") {
      this.$message({ message: text, type, duration: 3200, showClose: true });
    },
    async request(url, options = {}) {
      return apiRequest(url, options, {
        controllers: this.requestControllers,
        isStopped: () => this.pollingStopped,
      });
    },
    async loadConfiguration() {
      try {
        this.apiConfigured = Boolean(
          (await this.request("/api/health")).configured
        );
      } catch (error) {
        this.apiConfigured = false;
      }
    },
    pickSource(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (file) this.acceptSource(file);
    },
    dropSource(event) {
      this.sourceDragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.acceptSource(file);
    },
    pickReference(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (file) this.acceptReference(file);
    },
    dropReference(event) {
      this.referenceDragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.acceptReference(file);
    },
    validFile(file) {
      if (!IMAGE_TYPES.includes(file.type)) {
        this.message("仅支持 PNG、JPG 或 WebP 图片", "warning");
        return false;
      }
      if (file.size >= 10 * 1024 * 1024) {
        this.message("图片必须小于 10MB，请压缩后再上传", "warning");
        return false;
      }
      return true;
    },
    acceptSource(file) {
      if (!this.validFile(file)) return;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        Object.assign(this, {
          sourceFile: file,
          previewUrl: url,
          sourceImage: image,
          sourceWidth: image.naturalWidth,
          sourceHeight: image.naturalHeight,
          selectedRatio: this.closestRatio(
            image.naturalWidth,
            image.naturalHeight
          ),
          selection: null,
          draftSelection: null,
          lightingAnchor: "auto",
          resultUrl: "",
          viewMode: "mark",
          errorMessage: "",
        });
        this.$nextTick(this.drawCanvas);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        this.message("无法读取图片，请重新选择", "error");
      };
      image.src = url;
    },
    acceptReference(file) {
      if (!this.validFile(file)) return;
      if (this.replacementPreviewUrl)
        URL.revokeObjectURL(this.replacementPreviewUrl);
      this.replacementFile = file;
      this.replacementAssetReference = null;
      this.selectedFurnitureAsset = null;
      this.replacementPreviewUrl = URL.createObjectURL(file);
      this.referencePrepared = false;
      this.referencePrepareError = "";
      this.resultUrl = "";
      this.viewMode = "mark";
    },
    removeReference() {
      if (this.replacementPreviewUrl)
        URL.revokeObjectURL(this.replacementPreviewUrl);
      this.replacementFile = null;
      this.replacementAssetReference = null;
      this.selectedFurnitureAsset = null;
      this.replacementPreviewUrl = "";
      this.referencePrepared = false;
      this.referencePrepareError = "";
      this.resultUrl = "";
    },
    async selectFurnitureMaterial(asset) {
      if (!asset || !asset.url || this.furnitureMaterialLoading) return;
      this.furnitureLibraryVisible = false;
      this.furnitureMaterialLoading = true;
      try {
        if (this.replacementPreviewUrl && this.replacementPreviewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(this.replacementPreviewUrl);
        }
        this.replacementFile = null;
        this.replacementAssetReference = { url: asset.url, localUrl: asset.url, pending: true };
        this.replacementPreviewUrl = asset.url;
        this.selectedFurnitureAsset = asset;
        this.referencePrepared = false;
        this.referencePrepareError = "";
        this.resultUrl = "";
        this.viewMode = "mark";
        this.message(`已选择${this.materialLibraryLabel}素材：${asset.title}`, "success");
        try {
          this.replacementAssetReference = await this.registerFurnitureReference(asset);
        } catch (error) {
          // Retry registration immediately before AI writing or generation.
        }
      } catch (error) {
        this.message(error.message || `${this.materialLibraryLabel}素材添加失败，请重新选择`, "error");
      } finally {
        this.furnitureMaterialLoading = false;
      }
    },
    async registerFurnitureReference(asset = this.selectedFurnitureAsset) {
      if (!asset) return null;
      return this.request("/api/material-assets/reference", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assetId: asset.id, url: asset.url }),
        timeoutMs: 30000,
      });
    },
    async ensureFurnitureReference() {
      if (!this.selectedFurnitureAsset) return;
      try {
        this.replacementAssetReference = await this.registerFurnitureReference();
      } catch (error) {
        const unavailable = new Error(`${this.materialLibraryLabel}素材暂时无法加入生成任务`);
        unavailable.suggestion = `请稍后重试，或先使用本地上传的${this.replacementPreset.referenceLabel}参考图。`;
        throw unavailable;
      }
    },
    closestRatio(width, height) {
      const source = width / height;
      return RATIOS.reduce(
        (best, item) => {
          const parts = item.split(":").map(Number);
          const distance = Math.abs(Math.log(source / (parts[0] / parts[1])));
          return distance < best.distance ? { ratio: item, distance } : best;
        },
        { ratio: "16:9", distance: Infinity }
      ).ratio;
    },
    drawCanvas() {
      const canvas = this.$refs.replaceCanvas;
      if (!canvas || !this.sourceImage) return;
      const scale = Math.min(
        1,
        2200 / Math.max(this.sourceWidth, this.sourceHeight)
      );
      canvas.width = Math.max(1, Math.round(this.sourceWidth * scale));
      canvas.height = Math.max(1, Math.round(this.sourceHeight * scale));
      const context = canvas.getContext("2d");
      context.drawImage(this.sourceImage, 0, 0, canvas.width, canvas.height);
      const current = this.draftSelection || this.selection;
      if (current) {
        this.drawSelection(
          context,
          current,
          canvas.width,
          canvas.height,
          Boolean(this.draftSelection)
        );
      }
    },
    drawSelection(context, selection, width, height, draft) {
      const x = selection.x * width;
      const y = selection.y * height;
      const w = selection.width * width;
      const h = selection.height * height;
      context.save();
      context.fillStyle = draft ? "rgba(37,99,235,.14)" : "rgba(37,99,235,.22)";
      context.strokeStyle = "#2563eb";
      context.lineWidth = Math.max(3, width / 650);
      context.setLineDash(draft ? [12, 8] : []);
      context.fillRect(x, y, w, h);
      context.strokeRect(x, y, w, h);
      context.setLineDash([]);
      const size = Math.max(14, Math.min(30, width / 36));
      context.font = `700 ${size}px "Microsoft YaHei", sans-serif`;
      const label = this.isLightingFixture && !draft
        ? `${this.isAddition ? "新增灯具范围" : "灯具尺寸"} ${this.lightingTargetBounds.width}×${this.lightingTargetBounds.height}px`
        : this.operationAreaTitle;
      const labelWidth = context.measureText(label).width + size;
      const labelY = Math.max(0, y - size * 1.65);
      context.fillStyle = "#2563eb";
      context.fillRect(x, labelY, labelWidth, size * 1.65);
      context.fillStyle = "#fff";
      context.textBaseline = "middle";
      context.fillText(label, x + size / 2, labelY + size * 0.83);
      context.restore();
    },
    canvasPoint(event) {
      const rect = this.$refs.replaceCanvas.getBoundingClientRect();
      return {
        x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
        y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
      };
    },
    startSelection(event) {
      if (!this.sourceImage || this.generating || this.viewMode !== "mark")
        return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      this.drawingStart = this.canvasPoint(event);
      this.drawingPointerId = event.pointerId;
      this.isDrawing = true;
      this.draftSelection = null;
      this.$refs.replaceCanvas.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    moveSelection(event) {
      if (!this.isDrawing || event.pointerId !== this.drawingPointerId) return;
      const current = this.canvasPoint(event);
      this.draftSelection = {
        x: Math.min(this.drawingStart.x, current.x),
        y: Math.min(this.drawingStart.y, current.y),
        width: Math.abs(current.x - this.drawingStart.x),
        height: Math.abs(current.y - this.drawingStart.y),
      };
      this.drawCanvas();
    },
    endSelection(event) {
      if (!this.isDrawing || event.pointerId !== this.drawingPointerId) return;
      const replacingSelection = Boolean(this.selection);
      if (
        this.draftSelection &&
        this.draftSelection.width >= 0.015 &&
        this.draftSelection.height >= 0.015
      ) {
        this.selection = { ...this.draftSelection };
        this.resultUrl = "";
        if (this.isLightingFixture) {
          const bounds = selectionPixelBounds(this.selection, this.sourceWidth, this.sourceHeight);
          this.message(`${replacingSelection ? "已更新" : "已选定"}灯具区域：${bounds.width} × ${bounds.height} 像素`, "success");
        }
      } else if (this.draftSelection)
        this.message(this.isAddition ? "框选范围太小，请框住新产品要放置的位置" : `框选范围太小，请完整框住要更换的${this.replacementPreset.targetNoun}`, "warning");
      this.cancelSelection();
    },
    cancelSelection() {
      this.draftSelection = null;
      this.isDrawing = false;
      this.drawingPointerId = null;
      this.drawCanvas();
    },
    clearSelection() {
      this.selection = null;
      this.resultUrl = "";
      this.viewMode = "mark";
      this.$nextTick(this.drawCanvas);
      this.message(this.isAddition ? `请重新框选新${this.replacementPreset.targetNoun}的放置位置` : `请在图片上重新框选${this.replacementPreset.targetNoun}`, "info");
    },
    async upload(file) {
      const form = new FormData();
      form.append("file", file);
      return this.request("/api/images/upload", {
        method: "POST",
        body: form,
        timeoutMs: 120000,
      });
    },
    buildSelectionMaskFile() {
      if (!this.selection || !this.sourceWidth || !this.sourceHeight) {
        return Promise.reject(new Error("无法生成框选区域遮罩，请重新框选"));
      }
      const bounds = selectionPixelBounds(this.selection, this.sourceWidth, this.sourceHeight);
      const canvas = document.createElement("canvas");
      canvas.width = this.sourceWidth;
      canvas.height = this.sourceHeight;
      const context = canvas.getContext("2d");
      // Alpha=255 is protected and Alpha=0 is editable. The server uses this
      // same mask after generation to copy every pixel outside the selection
      // back from the original image, so the model cannot edit a similar object elsewhere.
      context.fillStyle = "rgba(255,255,255,1)";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.clearRect(bounds.left, bounds.top, bounds.width, bounds.height);
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error("框选区域遮罩生成失败，请重新框选"));
            return;
          }
          resolve(new File([blob], `selection-mask-${Date.now()}.png`, { type: "image/png" }));
        }, "image/png");
      });
    },
    buildSelectionGuideFile() {
      if (!this.selection || !this.sourceImage || !this.sourceWidth || !this.sourceHeight) {
        return Promise.reject(new Error("无法生成框选定位图，请重新框选"));
      }
      const bounds = selectionPixelBounds(this.selection, this.sourceWidth, this.sourceHeight);
      const canvas = document.createElement("canvas");
      canvas.width = this.sourceWidth;
      canvas.height = this.sourceHeight;
      const context = canvas.getContext("2d");
      context.drawImage(this.sourceImage, 0, 0, canvas.width, canvas.height);

      // This image is used only by the prompt-writing model. Keep the clean source
      // for generation, but make the selected object unmistakable to the vision model.
      context.fillStyle = "rgba(0,0,0,.28)";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(
        this.sourceImage,
        bounds.left,
        bounds.top,
        bounds.width,
        bounds.height,
        bounds.left,
        bounds.top,
        bounds.width,
        bounds.height
      );
      const lineWidth = Math.max(6, Math.round(Math.min(canvas.width, canvas.height) * 0.006));
      context.strokeStyle = "#ff2d2d";
      context.lineWidth = lineWidth;
      context.strokeRect(
        bounds.left - lineWidth / 2,
        bounds.top - lineWidth / 2,
        bounds.width + lineWidth,
        bounds.height + lineWidth
      );
      const centerX = bounds.left + bounds.width / 2;
      const centerY = bounds.top + bounds.height / 2;
      context.beginPath();
      context.arc(centerX, centerY, Math.max(8, lineWidth * 1.1), 0, Math.PI * 2);
      context.fillStyle = "#ff2d2d";
      context.fill();

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error("框选定位图生成失败，请重新框选"));
            return;
          }
          resolve(new File([blob], `selection-guide-${Date.now()}.png`, { type: "image/png" }));
        }, "image/png");
      });
    },
    async prepareReferenceImage() {
      if (this.referencePreparing || (!this.replacementFile && !this.replacementAssetReference)) return;
      this.referencePreparing = true;
      this.referencePrepareError = "";
      this.referencePrepareStatus = "正在使用百度智能云抠除商品背景";
      try {
        let sourceBlob;
        if (this.replacementFile) {
          sourceBlob = this.replacementFile;
        } else {
          await this.ensureFurnitureReference();
          sourceBlob = await downloadResource(this.replacementAssetReference.url, { timeoutMs: 120000 });
        }

        const form = new FormData();
        form.append("file", sourceBlob, sourceBlob.name || "product-source.png");
        const cleanedBlob = await postMultipartResource("/api/images/remove-background", form, {
          timeoutMs: 150000,
        });
        this.referencePrepareStatus = "正在保留原图并应用透明蒙版";
        // 百度只返回主体 mask，后端将其应用到原图；不要再经 Canvas 解码重绘。
        const preparedFile = new File([cleanedBlob], "product-transparent.png", { type: "image/png" });

        if (this.replacementPreviewUrl && this.replacementPreviewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(this.replacementPreviewUrl);
        }
        this.replacementFile = preparedFile;
        this.replacementAssetReference = null;
        this.selectedFurnitureAsset = null;
        this.replacementPreviewUrl = URL.createObjectURL(preparedFile);
        this.referencePrepared = true;
        this.referencePrepareStatus = "";
        this.resultUrl = "";
        this.viewMode = "mark";
        this.message("已生成原尺寸透明商品素材，商品内容保持原图", "success");
      } catch (error) {
        this.referencePrepareError = error.message || "一键抠图失败，请更换一张主体更清晰的素材";
        this.message(this.referencePrepareError, "error");
      } finally {
        this.referencePreparing = false;
      }
    },
    openMaskedEditor(sourceUrl, width = 0, height = 0) {
      if (!sourceUrl || this.generating) return;
      this.maskedEdit = {
        visible: true,
        sourceUrl,
        candidateUrl: "",
        width: Number(width || this.sourceWidth || 0),
        height: Number(height || this.sourceHeight || 0),
        submitting: false,
        error: "",
        iteration: 0,
      };
    },
    closeMaskedEditor() {
      if (this.maskedEdit.submitting) return;
      this.maskedEdit = {
        visible: false,
        sourceUrl: "",
        candidateUrl: "",
        width: 0,
        height: 0,
        submitting: false,
        error: "",
        iteration: 0,
      };
    },
    maskedEditRequestId() {
      const random =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return `object-mask-${random}`.slice(0, 64);
    },
    async waitForMaskedEditTaskIds(batchId) {
      const startedAt = Date.now();
      while (Date.now() - startedAt < 90000 && !this.pollingStopped) {
        const record = await this.request(
          `/api/records/${encodeURIComponent(batchId)}`,
          { timeoutMs: 15000, retries: 1 }
        );
        const taskIds = (record.tasks || [])
          .map((task) => task && task.id)
          .filter(Boolean);
        if (taskIds.length) return taskIds;
        if (record.status === "failed") {
          const error = new Error(record.errorMessage || "AI 局部编辑任务创建失败");
          error.terminal = true;
          throw error;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
      }
      const error = new Error("AI 局部编辑任务已保存，暂时还未取得任务编号");
      error.suggestion = "请稍后从生成记录中重新进入 AI 局部编辑，无需重复提交。";
      throw error;
    },
    async refreshHistoryForNewRecord() {
      this.historyPage = 1;
      const startedAt = Date.now();
      while (this.historyLoading && Date.now() - startedAt < 5000) {
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      await this.loadHistory();
    },
    async submitMaskedEdit(payload) {
      if (
        this.maskedEdit.submitting ||
        this.generating ||
        !payload ||
        !payload.sourceUrl ||
        !payload.blob ||
        !payload.prompt
      ) return;

      this.maskedEdit.submitting = true;
      this.maskedEdit.error = "";
      let batchId = "";
      try {
        const markedFile = new File(
          [payload.blob],
          `local-edit-marked-${Date.now()}.png`,
          { type: "image/png" }
        );
        const markedAsset = await this.upload(markedFile);
        const batch = await this.request("/api/generations", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            imageUrls: [markedAsset.url],
            prompt: payload.prompt,
            space: "室内空间",
            style: this.replacementPreset.label,
            size: this.closestRatio(payload.width, payload.height),
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: "object_replace",
            floorPlanRecordUrls: [],
            referenceRecordUrls: [],
            annotation: null,
            requestId: this.maskedEditRequestId(),
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: "创建 AI 局部编辑任务等待超时",
        });
        batchId = batch.batchId || "";
        if (batchId) {
          this.message("提交成功，已进入后台生成", "success");
          await this.refreshHistoryForNewRecord();
        }
        let taskIds = (batch.tasks || [])
          .map((task) => task && task.id)
          .filter(Boolean);
        if (!taskIds.length && batchId) {
          taskIds = await this.waitForMaskedEditTaskIds(batchId);
        }
        if (taskIds.length !== 1) throw new Error("AI 局部编辑任务创建失败，请稍后重试");

        const resultUrl = (await this.pollTasks(taskIds))[0] || "";
        if (!resultUrl) throw new Error("AI 局部编辑未返回可用图片");
        this.resultUrl = resultUrl;
        this.sourceWidth = Number(payload.width || this.sourceWidth || 0);
        this.sourceHeight = Number(payload.height || this.sourceHeight || 0);
        this.viewMode = "result";
        this.maskedEdit.submitting = false;
        this.closeMaskedEditor();
        this.message("局部修改已完成并替换当前结果", "success");
        await this.refreshHistoryForNewRecord();
      } catch (error) {
        this.maskedEdit.error =
          [error.message || "AI 局部编辑失败", error.suggestion]
            .filter(Boolean)
            .join("。 ");
        this.message(error.message || "AI 局部编辑失败", "error");
        if (batchId) await this.refreshHistoryForNewRecord();
      } finally {
        this.maskedEdit.submitting = false;
      }
    },
    async writeReplacementDescription() {
      if (this.writingReplacementDescription || this.generating) return;
      if (!this.sourceFile || !this.sourceImage) {
        return this.message("请先上传空间图片", "warning");
      }
      if (!this.selection) {
        return this.message(`请先框选要更换的${this.replacementPreset.targetNoun}`, "warning");
      }
      if (!this.apiConfigured) {
        return this.message("请先配置 ToAPIs API Key", "warning");
      }

      this.writingReplacementDescription = true;
      const previousDescription = this.replacementDescription;
      try {
        await this.ensureFurnitureReference();
        const guideFile = await this.buildSelectionGuideFile();
        const files = [guideFile];
        if (this.replacementFile) files.push(this.replacementFile);
        const assets = await Promise.all(files.map((file) => this.upload(file)));
        if (this.replacementAssetReference) assets.push(this.replacementAssetReference);
        const result = await this.request("/api/prompts/write-object-replacement", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            imageUrls: assets.map((asset) => asset.url).filter(Boolean),
            floorPlanImageUrls: [],
            layoutNote: previousDescription.trim(),
            viewpoint: this.selectionGeometryPrompt,
            space: "室内空间",
            style: this.replacementPreset.label,
          }),
          timeoutMs: 120000,
          timeoutMessage: "AI 写作等待超时，请稍后重试",
        });
        const writtenDescription = String(result.prompt || "").trim();
        if (!writtenDescription) throw new Error("AI 未返回可用的替换描述");
        this.replacementDescription = writtenDescription.slice(0, 300);
        this.message("AI 已根据原图和框选区域完成描述", "success");
      } catch (error) {
        this.replacementDescription = previousDescription;
        this.message(error.message || "AI 写作失败，请稍后重试", "error");
      } finally {
        this.writingReplacementDescription = false;
      }
    },
    requestId() {
      const random =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return `object-replace-${this.replacementType}-${random}`.slice(0, 64);
    },
    handlePrimaryAction() {
      return this.submissionPending
        ? this.recoverPendingGeneration()
        : this.generateReplacement();
    },
    finishGeneration(resultUrl) {
      this.resultUrl = resultUrl || "";
      this.viewMode = "result";
      this.progress = 100;
      this.generationText = `${this.replacementPreset.label}已完成`;

      // A completed job belongs to history. Release its live context immediately so
      // the next click creates a fresh generation instead of recovering the old one.
      this.currentBatchId = "";
      this.currentTaskIds = [];
      this.errorMessage = "";
      this.errorSuggestion = "";
      this.errorRetryable = false;
    },
    async generateReplacement() {
      if (this.generating || this.writingReplacementDescription) return;
      if (!this.sourceFile) return this.message("请先上传空间图片", "warning");
      if (!this.selection)
        return this.message(this.isAddition ? `请先框选新${this.replacementPreset.targetNoun}的放置位置` : `请先框选需要更换的${this.replacementPreset.targetNoun}`, "warning");
      if (!this.hasReplacementTarget)
        return this.message(`请上传${this.isAddition ? "产品" : "替换"}参考图或填写${this.operationActionText}描述`, "warning");
      if (!this.apiConfigured)
        return this.message("请先配置 ToAPIs API Key", "warning");
      Object.assign(this, {
        generating: true,
        errorMessage: "",
        errorSuggestion: "",
        errorRetryable: false,
        resultUrl: "",
        viewMode: "mark",
        progress: 4,
        generationText: "正在准备干净原图",
      });
      const requestId = this.requestId();
      const pendingId = `interior-${requestId}`;
      const createdAt = new Date().toISOString();
      this.currentBatchId = pendingId;
      this.currentTaskIds = [];
      const pending = {
        id: pendingId,
        status: "generating",
        createdAt,
        taskIds: [],
        source: this.previewUrl,
        result: "",
      };
      this.savePending(pendingId, createdAt);
      this.history = [
        pending,
        ...this.history.filter((item) => item.id !== pendingId),
      ].slice(0, 8);
      let submitted = false;
      try {
        await this.ensureFurnitureReference();
        const files = [this.sourceFile];
        if (this.replacementFile) files.push(this.replacementFile);
        this.progress = 8;
        this.generationText = "正在上传图片";
        // Keep the component context for each upload; passing the method directly to map loses `this`.
        const assets = await Promise.all(files.map((file) => this.upload(file)));
        if (this.replacementAssetReference) assets.push(this.replacementAssetReference);
        if (
          assets.length > 1 &&
          !this.replacementDescription.trim() &&
          this.replacementType === "generic"
        ) {
          this.generationText = this.isLightingFixture
            ? "正在识别参考灯具完整造型"
            : "正在识别参考物完整类型与结构";
          try {
            const guideAsset = await this.upload(await this.buildSelectionGuideFile());
            const helperImageUrls = [guideAsset.url, assets[1] && assets[1].url]
              .filter(Boolean);
            const described = await this.request("/api/prompts/write-object-replacement", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                imageUrls: helperImageUrls,
                floorPlanImageUrls: [],
                layoutNote: "请先准确判断参考物的真实类别，再识别完整产品外轮廓、组成部件、数量、连接结构和主体宽高比。若为灯具，必须写明灯罩、灯头、吊线或支架、安装盘等完整结构，禁止简化或漏掉部件。",
                viewpoint: this.selectionGeometryPrompt,
                space: "室内空间",
                style: "灯具完整造型替换",
              }),
              timeoutMs: 120000,
              timeoutMessage: "参考灯具识别超时，将使用原参考图继续生成",
            });
            const description = String(described.prompt || "").trim();
            if (description) this.replacementDescription = description.slice(0, 300);
          } catch (error) {
            // The reference image and hard constraints still allow generation when visual writing is unavailable.
          }
        }
        // The normalized selection is useful prompt context, but it is not a hard
        // spatial constraint for an image model. Always submit the pixel-accurate
        // alpha mask as well so the selected bedside object is the only editable area.
        // The backend also uses this mask to restore every outside pixel from the
        // original image after generation.
        this.generationText = "正在锁定框选位置";
        const maskAsset = await this.upload(await this.buildSelectionMaskFile());
        const referenceRecordUrls = assets.slice(1).map((asset) => asset.localUrl).filter(Boolean);
        pending.source = assets[0].localUrl || this.previewUrl;
        this.savePending(pendingId, createdAt, assets);
        this.progress = 16;
        this.generationText = "正在创建替换任务";
        submitted = true;
        const batch = await this.request("/api/generations", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            imageUrls: assets.map((asset) => asset.url),
            maskUrl: maskAsset.url,
            prompt: this.finalPrompt,
            space: "室内空间",
            style: this.replacementPreset.label,
            size: this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: "object_replace",
            floorPlanRecordUrls: assets[0] && assets[0].localUrl ? [assets[0].localUrl] : [],
            referenceRecordUrls,
            annotation: {
              ...this.selection,
              layoutNote: this.replacementDescription.trim() || null,
            },
            requestId,
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: "创建替换任务等待超时，正在确认是否已经提交",
        });
        const taskIds = (batch.tasks || [])
          .map((task) => task.id)
          .filter(Boolean);
        this.currentBatchId = batch.batchId || pendingId;
        pending.id = this.currentBatchId;
        if (taskIds.length !== 1) {
          const error = new Error("任务已保存，正在后台确认任务编号");
          error.suggestion = "系统会自动查询任务状态，也可点击“查询任务状态”，无需重复提交。";
          error.pending = true;
          error.retryable = true;
          throw error;
        }
        this.currentTaskIds = taskIds;
        pending.taskIds = taskIds;
        removePendingGeneration(pendingId);
        this.progress = 20;
        this.generationText = "AI 正在替换并融合";
        this.finishGeneration((await this.pollTasks(taskIds))[0] || "");
        this.message(`${this.replacementPreset.label}已完成`, "success");
        await this.loadHistory();
      } catch (error) {
        if (submitted && (error.retryable || error.pending)) {
          this.scheduleRefresh();
        } else if (!pending.taskIds.length) {
          removePendingGeneration(pending.id);
          this.history = this.history.filter((item) => item.id !== pending.id);
          this.currentBatchId = "";
          this.currentTaskIds = [];
        }
        this.errorMessage = error.message || `${this.replacementPreset.label}失败`;
        this.errorSuggestion =
          error.suggestion || `请检查${this.replacementPreset.areaTitle}、${this.replacementPreset.targetTitle}和网络后重试。`;
        this.errorRetryable = Boolean(error.pending || error.retryable);
        this.message(this.errorMessage, error.pending ? "warning" : "error");
      } finally {
        this.generating = false;
      }
    },
    async recoverPendingGeneration() {
      if (this.generating) return;
      if (this.currentTaskIds.length) return this.continuePolling();
      if (!this.currentBatchId) return;

      let recoveredTaskIds = [];
      let shouldPoll = false;
      this.generating = true;
      this.generationText = "正在确认后台任务";
      this.progress = Math.max(16, this.progress);
      this.errorMessage = "";
      this.errorSuggestion = "";
      this.errorRetryable = false;
      try {
        const record = await this.request(
          `/api/records/${encodeURIComponent(this.currentBatchId)}`,
          { timeoutMs: 15000, retries: 2, timeoutMessage: "查询后台任务状态超时" }
        );
        recoveredTaskIds = (record.tasks || [])
          .map((task) => task && task.id)
          .filter(Boolean);
        if (record.status === "failed") {
          const failedTask = (record.tasks || []).find(
            (task) => task && task.status === "failed"
          );
          const details = failedTask && failedTask.error;
          const error = new Error(
            (details && details.message) ||
              record.errorMessage ||
              `${this.replacementPreset.label}任务提交失败`
          );
          error.suggestion =
            (details && details.suggestion) || "请稍后重新创建任务。";
          error.retryable = Boolean(details && details.retryable);
          error.terminal = true;
          throw error;
        }
        if (!recoveredTaskIds.length) {
          this.errorMessage = "任务已保存，AI 服务正在确认任务编号";
          this.errorSuggestion = "系统会继续自动查询，请稍后再点“查询任务状态”，无需重复提交。";
          this.errorRetryable = true;
          this.scheduleRefresh();
          await this.loadHistory();
          return;
        }
        this.currentTaskIds = recoveredTaskIds;
        shouldPoll = true;
        await this.loadHistory();
      } catch (error) {
        this.errorMessage = error.message || "暂时无法查询后台任务";
        this.errorSuggestion =
          error.suggestion || "请稍后继续查询，无需重复提交。";
        this.errorRetryable = error.terminal
          ? Boolean(error.retryable)
          : true;
        if (!error.terminal) this.scheduleRefresh();
      } finally {
        this.generating = false;
      }
      if (shouldPoll) await this.continuePolling();
    },
    savePending(id, createdAt, assets = []) {
      savePendingGeneration({
        id,
        space: "室内空间",
        style: this.replacementPreset.label,
        size: this.selectedRatio,
        prompt: this.finalPrompt,
        status: "generating",
        stage: "object_replace",
        progress: 0,
        expectedCount: 1,
        tasks: [],
        images: [],
        inputContext: {
          floorPlanImages: assets.length && assets[0].localUrl
            ? [{
                type: "original",
                label: "原始图片（无标记）",
                url: assets[0].localUrl,
              }]
            : [],
          referenceImages: assets
            .slice(1)
            .map((asset, index) => ({
              type: "reference",
              label: `替换参考图 ${index + 1}`,
              url: asset.localUrl,
            }))
            .filter((image) => image.url),
          annotation: {
            ...this.selection,
            layoutNote: this.replacementDescription.trim() || null,
          },
        },
        createdAt,
      });
    },
    async pollTasks(taskIds) {
      const startedAt = Date.now();
      let failures = 0;
      while (Date.now() - startedAt < 600000 && !this.pollingStopped) {
        try {
          const states = await Promise.all(
            taskIds.map((id) =>
              this.request(`/api/generations/${encodeURIComponent(id)}`, {
                timeoutMs: 12000,
                retries: 1,
              })
            )
          );
          failures = 0;
          const average =
            states.reduce(
              (sum, item) =>
                sum +
                (item.status === "completed"
                  ? 100
                  : Number(item.progress || 0)),
              0
            ) / states.length;
          this.progress = Math.max(
            20,
            Math.min(99, Math.round(20 + average * 0.79))
          );
          this.generationText = `AI 正在替换并融合 ${Math.round(average)}%`;
          if (
            states.every((item) =>
              ["completed", "failed"].includes(item.status)
            )
          ) {
            const failed = states.find((item) => item.status === "failed");
            if (failed) {
              const error = new Error(
                failed.error && failed.error.message
                  ? failed.error.message
                  : `${this.replacementPreset.label}任务失败`
              );
              Object.assign(error, failed.error || {}, { terminal: true });
              throw error;
            }
            const images = states.map((item) =>
              item.result && item.result.data && item.result.data[0]
                ? item.result.data[0].url
                : ""
            );
            if (images.some((image) => !image)) {
              const error = new Error(
                "任务已完成，但 AI 服务未返回可用图片"
              );
              Object.assign(error, {
                code: "EMPTY_GENERATION_RESULT",
                suggestion: "任务记录已保留，请联系管理员并提供任务编号。",
                retryable: false,
                terminal: true,
              });
              throw error;
            }
            return images;
          }
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error;
          failures += 1;
          if (failures >= 5) {
            const pending = new Error(
              "暂时无法查询进度，但任务可能仍在后台处理"
            );
            Object.assign(pending, {
              pending: true,
              retryable: true,
              suggestion: "请检查网络后点击“继续查询”，不需要重复提交。",
            });
            throw pending;
          }
        }
        await new Promise((resolve) => window.setTimeout(resolve, 3000));
      }
      const pending = new Error("生成时间较长，任务仍在后台处理中");
      Object.assign(pending, {
        pending: true,
        retryable: true,
        suggestion: "请稍后点击“继续查询”。",
      });
      throw pending;
    },
    async continuePolling() {
      if (this.generating || !this.currentTaskIds.length) return;
      this.generating = true;
      this.errorMessage = "";
      this.errorSuggestion = "";
      this.errorRetryable = false;
      try {
        this.finishGeneration((await this.pollTasks(this.currentTaskIds))[0] || "");
        this.message(`${this.replacementPreset.label}已完成，可以继续生成`, "success");
        await this.loadHistory();
      } catch (error) {
        this.errorMessage = error.message || "查询任务失败";
        this.errorSuggestion = error.suggestion || "请稍后重试。";
        this.errorRetryable = Boolean(error.pending || error.retryable);
        if (error.terminal) {
          this.currentTaskIds = [];
          await this.loadHistory();
        }
      } finally {
        this.generating = false;
      }
    },
    async loadHistory() {
      if (this.historyLoading) return;
      this.historyLoading = true;
      try {
        const styleFilter = this.replacementType === "generic" ? "" : `&style=${encodeURIComponent(this.replacementPreset.label)}`;
        const response = await this.request(`/api/records/paged?stage=object_replace${styleFilter}&page=${this.historyPage}&pageSize=12`);
        this.historyTotal = Number(response.total || 0);
        const records = response.items || [];
        this.history = (this.historyPage === 1 ? mergePendingGenerations(records, "object_replace") : records)
          .filter((record) => record.stage === "object_replace")
          .filter((record) => this.replacementType === "generic" || record.style === this.replacementPreset.label)
          .slice(0, 12)
          .map((record) => {
            const sources =
              record.inputContext && record.inputContext.floorPlanImages
                ? record.inputContext.floorPlanImages
                : [];
            const refs =
              record.inputContext && record.inputContext.referenceImages
                ? record.inputContext.referenceImages
                : [];
            const legacySource = refs.find((item) =>
              String(item.label || "").includes("原始")
            ) || (refs.length > 1 ? refs[0] : null);
            return {
              id: record.id,
              localOnly: Boolean(record.localOnly),
              isLocalEdit: Boolean(
                record.maskedEdit || String(record.id || "").includes("object-mask-")
              ),
              status: record.status,
              createdAt: record.createdAt,
              taskIds: (record.tasks || [])
                .map((task) => task.id)
                .filter(Boolean),
              taskStates: record.tasks || [],
              errorMessage: record.error || record.errorMessage || "",
              imageModel: record.imageModel || "",
              source: sources[0] ? sources[0].url : (legacySource ? legacySource.url : ""),
              result:
                record.images && record.images[0]
                  ? record.images[0].url || record.images[0]
                  : "",
              resultWidth: record.images && record.images[0] ? record.images[0].width : null,
              resultHeight: record.images && record.images[0] ? record.images[0].height : null,
            };
          });
        const active = this.history.find(
          (record) => record.status === "generating" && record.taskIds.length
        );
        if (
          active &&
          !this.generating &&
          !this.maskedEdit.submitting &&
          !this.currentTaskIds.length
        ) {
          this.currentBatchId = active.id;
          this.currentTaskIds = active.taskIds;
          this.continuePolling();
        }
        this.historyLoadErrorNotified = false;
        this.scheduleRefresh();
      } catch (error) {
        if (!this.history.length) this.history = [];
        if (!this.historyLoadErrorNotified) {
          this.historyLoadErrorNotified = true;
          this.message(
            `${error.message || "暂时无法加载替换记录"}。${error.suggestion || `${this.replacementPreset.label}仍可继续使用，请稍后点击刷新记录。`}`,
            "warning"
          );
        }
      } finally {
        this.historyLoading = false;
      }
    },
    scheduleRefresh() {
      if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer);
      if (
        this.pollingStopped ||
        !this.history.some(
          (record) => record.status === "generating" && !record.taskIds.length
        )
      )
        return;
      this.pendingRecordTimer = window.setTimeout(() => {
        this.pendingRecordTimer = null;
        this.loadHistory();
      }, 2000);
    },
    changeHistoryPage(page) {
      this.historyPage = page;
      this.loadHistory();
    },
    previewHistoryImage(record, type) {
      const image = type === "result" ? record.result : record.source;
      if (!image) return;
      this.$emit("preview-image", image, {
        title: type === "result" ? `${this.replacementPreset.label} · 替换后` : `${this.replacementPreset.label} · 替换前`,
        items: this.viewerItems,
        index: this.viewerItems.findIndex(
          (item) => item.key === `${record.id}-${type}`
        ),
      });
    },
    async removeHistory(record) {
      try {
        await this.$confirm(
          `删除这条${this.replacementPreset.label}记录后将无法恢复，是否继续？`,
          "删除处理记录",
          { type: "warning" }
        );
      } catch (error) {
        return;
      }
      try {
        if (!record.localOnly) {
          await this.request(`/api/records/${encodeURIComponent(record.id)}`, {
            method: "DELETE",
          });
        }
        removePendingGeneration(record.id);
        this.history = this.history.filter((item) => item.id !== record.id);
        this.message("记录已删除", "success");
      } catch (error) {
        if (error.status === 404) {
          removePendingGeneration(record.id);
          this.history = this.history.filter((item) => item.id !== record.id);
          this.message("记录已删除", "success");
          return;
        }
        this.message(error.message || "删除失败", "error");
      }
    },
    statusLabel(status) {
      return (
        { completed: "已完成", generating: "处理中", failed: "失败" }[status] ||
        "处理中"
      );
    },
    formatTime(value) {
      const date = new Date(value);
      if (!value || Number.isNaN(date.getTime())) return "";
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
        date.getDate()
      ).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;
    },
    async copyFinalPrompt() {
      try {
        await navigator.clipboard.writeText(this.finalPrompt);
        this.message("生成图片提示词已复制", "success");
      } catch (error) {
        const textarea = document.createElement("textarea");
        textarea.value = this.finalPrompt;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);
        this.message(
          copied ? "生成图片提示词已复制" : "复制失败，请手动选择提示词",
          copied ? "success" : "warning"
        );
      }
    },
    async downloadResult() {
      if (!this.resultUrl) return;
      try {
        const blob = await downloadResource(this.resultUrl)
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement("a")
        Object.assign(link, {
          href: objectUrl,
          download: `object-replacement-${Date.now()}.png`,
        })
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)
      } catch (error) {
        this.message(error.message || "下载失败，请稍后重试", "error")
      }
    },
  },
};
</script>

<style scoped>
@media not all {
  .object-replace {
    width: calc(100% - 68px);
    max-width: none;
    margin: 26px auto 64px;
    color: #26322b;
  }
  .intro {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 28px;
    margin-bottom: 18px;
  }
  .intro > div > span,
  .history header span {
    color: #e86420;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1.8px;
  }
  .intro h2 {
    margin: 7px 0 6px;
    font-size: 22px;
  }
  .intro p {
    max-width: 760px;
    margin: 0;
    color: #7b867f;
    font-size: 11px;
    line-height: 1.7;
  }
  .intro > em {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 1px solid #dce5df;
    border-radius: 5px;
    padding: 8px 11px;
    background: #fff;
    color: #728078;
    font-size: 9px;
    font-style: normal;
    font-weight: 700;
    white-space: nowrap;
  }
  .intro > em i {
    color: #2d8a52;
  }
  .object-replace-layout {
    display: grid;
    grid-template-columns: 370px minmax(0, 1fr);
    gap: 14px;
    min-height: 680px;
  }
  .controls,
  .stage {
    min-width: 0;
    overflow: hidden;
    border: 1px solid #e1e6e2;
    border-radius: 9px;
    background: #fff;
    box-shadow: 0 10px 28px rgba(37, 51, 43, 0.04);
  }
  .controls {
    padding: 4px 17px 18px;
    overflow-y: auto;
  }
  .controls section {
    padding: 16px 0;
    border-bottom: 1px solid #edf0ee;
  }
  .controls section:last-child {
    border: 0;
  }
  .controls :deep(.step-title) {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
  .controls :deep(.step-title > span) {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    flex: 0 0 28px;
    border-radius: 6px;
    background: #fff1e8;
    color: #e86420;
    font-size: 10px;
    font-weight: 800;
  }
  .controls :deep(.step-title > div) {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .controls :deep(.step-title strong) {
    font-size: 12px;
  }
  .controls :deep(.step-title small) {
    color: #949d97;
    font-size: 9px;
  }
  .upload {
    position: relative;
    height: 134px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 6px;
    overflow: hidden;
    border: 1px dashed #ccd5cf;
    border-radius: 7px;
    background: #fafbfa;
    color: #78847c;
    cursor: pointer;
    transition: 0.2s;
  }
  .upload:hover,
  .upload.dragging {
    border-color: #ed7b3f;
    background: #fff8f4;
  }
  .upload input {
    display: none;
  }
  .upload strong {
    color: #536058;
    font-size: 10px;
  }
  .upload strong em,
  .description-label em {
    color: #a2aaa5;
    font-size: 8px;
    font-style: normal;
  }
  .upload small {
    font-size: 8px;
  }
  .upload.filled img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .upload-icon {
    color: #e37943;
    font-size: 25px;
  }
  .upload b {
    position: absolute;
    right: 8px;
    bottom: 8px;
    border-radius: 4px;
    padding: 5px 8px;
    background: rgba(28, 36, 31, 0.78);
    color: #fff;
    font-size: 8px;
  }
  .reference {
    height: 112px;
    margin-bottom: 12px;
  }
  .remove-reference {
    position: absolute;
    top: 7px;
    right: 7px;
    width: 24px;
    height: 24px;
    border: 0;
    border-radius: 50%;
    background: rgba(30, 35, 32, 0.75);
    color: #fff;
    cursor: pointer;
  }
  .selection-state {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid #e1e6e2;
    border-radius: 7px;
    padding: 11px;
    background: #fafbfa;
  }
  .selection-state > i {
    width: 31px;
    height: 31px;
    display: grid;
    place-items: center;
    border-radius: 6px;
    background: #f0f2f1;
    color: #89928d;
  }
  .selection-state > div {
    min-width: 0;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 3px;
  }
  .selection-state strong {
    font-size: 10px;
  }
  .selection-state small {
    color: #8b958f;
    font-size: 8px;
  }
  .selection-state.ready {
    border-color: #fac6a9;
    background: #fff8f4;
  }
  .selection-state.ready > i {
    background: #ffeadf;
    color: #df5e1b;
  }
  .selection-state button {
    border: 0;
    background: none;
    color: #d65a1b;
    font-size: 9px;
  }
  .description-label {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
    color: #4b584f;
    font-size: 9px;
    font-weight: 700;
  }
  .description-label small {
    color: #a0a8a3;
    font-weight: 400;
  }
  .controls textarea {
    width: 100%;
    height: 82px;
    resize: none;
    box-sizing: border-box;
    border: 1px solid #dfe5e1;
    border-radius: 6px;
    padding: 10px;
    outline: none;
    color: #344139;
    font-family: inherit;
    font-size: 11px;
    line-height: 1.6;
  }
  .controls textarea:focus {
    border-color: #ea8049;
    box-shadow: 0 0 0 3px rgba(234, 128, 73, 0.1);
  }
  .quick-prompts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .quick-prompts button {
    border: 1px solid #e3e7e4;
    border-radius: 12px;
    padding: 4px 8px;
    background: #fff;
    color: #758078;
    font-size: 8px;
  }
  .stage {
    display: flex;
    flex-direction: column;
    background: #f5f6f5;
  }
  .stage-toolbar {
    min-height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: #fff;
    border-bottom: 1px solid #e1e5e2;
  }
  .stage-toolbar > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .stage-toolbar strong {
    font-size: 12px;
  }
  .stage-toolbar small {
    color: #8d9690;
    font-size: 8px;
  }
  .view-switch {
    display: flex;
    gap: 5px;
    padding: 3px;
    border-radius: 6px;
    background: #f0f2f1;
  }
  .view-switch button {
    border: 0;
    border-radius: 4px;
    padding: 7px 11px;
    background: transparent;
    color: #778179;
    font-size: 9px;
  }
  .view-switch button.active {
    background: #fff;
    color: #df5d1a;
    box-shadow: 0 1px 4px rgba(35, 45, 39, 0.12);
  }
  .view-switch button:disabled {
    opacity: 0.4;
  }
  .canvas-shell {
    min-height: 540px;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    padding: 18px;
  }
  .canvas-shell canvas {
    max-width: 100%;
    max-height: 560px;
    display: block;
    background: #e8ebe9;
    box-shadow: 0 12px 30px rgba(35, 45, 39, 0.16);
    cursor: crosshair;
    touch-action: none;
  }
  .placeholder {
    display: flex;
    align-items: center;
    flex-direction: column;
    color: #89928d;
    text-align: center;
  }
  .placeholder > span {
    width: 70px;
    height: 70px;
    display: grid;
    place-items: center;
    margin-bottom: 15px;
    border: 1px dashed #cbd2ce;
    border-radius: 14px;
    background: #fff;
    color: #dc7139;
    font-size: 28px;
  }
  .placeholder strong {
    color: #5e6962;
    font-size: 13px;
  }
  .placeholder p {
    font-size: 9px;
  }
  .canvas-hint {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 18px;
    padding: 8px 13px;
    background: rgba(31, 39, 34, 0.82);
    color: #fff;
    font-size: 9px;
    pointer-events: none;
  }
  .result {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .result img {
    max-width: 100%;
    max-height: 560px;
    object-fit: contain;
    box-shadow: 0 12px 30px rgba(35, 45, 39, 0.16);
  }
  .result span {
    position: absolute;
    top: 12px;
    right: 12px;
    border-radius: 16px;
    padding: 7px 10px;
    background: rgba(39, 123, 73, 0.9);
    color: #fff;
    font-size: 8px;
  }
  .stage footer {
    min-height: 47px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #e0e5e1;
    padding: 8px 14px;
    background: #fff;
  }
  .stage footer > div {
    display: flex;
    gap: 15px;
    color: #7c8680;
    font-size: 8px;
  }
  .stage footer i {
    color: #df6b2e;
  }
  .stage footer button {
    border: 1px solid #eab28f;
    border-radius: 4px;
    padding: 6px 9px;
    background: #fff7f2;
    color: #d65a1b;
    font-size: 8px;
  }
  .error-card {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 14px 14px;
    border: 1px solid #f0c9c4;
    border-radius: 6px;
    padding: 10px;
    background: #fff7f6;
    color: #b94337;
  }
  .error-card > div {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 3px;
  }
  .error-card small {
    font-size: 8px;
  }
  .error-card button {
    border: 0;
    background: none;
    color: #b94337;
  }
  .error-card.processing {
    border-color: #e5c36f;
    background: #fff8e6;
    color: #875a08;
  }
  .error-card.processing small {
    color: #765f31;
  }
  .error-card.processing button {
    color: #875a08;
  }
  .generate-bar {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) auto auto auto minmax(
        170px,
        220px
      );
    align-items: center;
    gap: 18px;
    margin-top: 14px;
    border: 1px solid #e1e6e2;
    border-radius: 9px;
    padding: 15px 17px;
    background: #fff;
  }
  .generate-intro {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .generate-intro > span {
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    background: #fff0e7;
    color: #dc5f1e;
  }
  .generate-intro > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .generate-intro strong {
    font-size: 11px;
  }
  .generate-intro small,
  .cost small {
    color: #8d9690;
    font-size: 8px;
  }
  .readiness {
    display: flex;
    gap: 8px;
  }
  .readiness span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 12px;
    padding: 5px 8px;
    background: #f1f3f2;
    color: #929a95;
    font-size: 8px;
  }
  .readiness span.ready {
    background: #edf7f0;
    color: #318452;
  }
  .cost {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
  }
  .cost strong {
    font-size: 10px;
    white-space: nowrap;
  }
  .cost i {
    width: 8px;
    height: 8px;
    display: inline-block;
    border-radius: 50%;
    background: #f1a33e;
  }
  .generate {
    min-height: 54px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 0;
    border-radius: 7px;
    background: linear-gradient(135deg, #f07836, #d85517);
    color: #fff;
    box-shadow: 0 8px 18px rgba(214, 85, 23, 0.2);
  }
  .generate:disabled {
    background: #c9cecb;
    box-shadow: none;
  }
  .generate > span:not(.spinner) {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
  }
  .generate strong {
    font-size: 11px;
  }
  .generate small {
    color: rgba(255, 255, 255, 0.78);
    font-size: 8px;
  }
  .spinner {
    width: 15px;
    height: 15px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  .history {
    margin-top: 26px;
  }
  .history > header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .history h2 {
    margin: 5px 0 3px;
    font-size: 18px;
  }
  .history header small {
    color: #8d9690;
    font-size: 9px;
  }
  .history header button {
    width: 30px;
    height: 30px;
    border: 1px solid #dfe5e1;
    border-radius: 5px;
    background: #fff;
  }
  .history-empty {
    min-height: 145px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 6px;
    border: 1px dashed #d9dfdb;
    border-radius: 8px;
    background: #fafbfa;
    color: #8b948f;
  }
  .history-empty > i {
    font-size: 23px;
  }
  .history-empty strong {
    font-size: 11px;
  }
  .history-empty small {
    font-size: 8px;
  }
  .history-list {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }
  .history article {
    overflow: hidden;
    border: 1px solid #e1e6e2;
    border-radius: 8px;
    background: #fff;
  }
  .history-meta {
    display: flex;
    justify-content: space-between;
    padding: 8px 9px;
  }
  .history-meta span {
    border-radius: 9px;
    padding: 3px 6px;
    background: #eef7f1;
    color: #2f8450;
    font-size: 7px;
  }
  .history-meta span.generating {
    background: #fff5e8;
    color: #c87820;
  }
  .history-meta span.failed {
    background: #fff0ee;
    color: #c44d42;
  }
  .history-meta small {
    color: #9aa29d;
    font-size: 7px;
  }
  .history-images {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    height: 105px;
    background: #e7ebe8;
  }
  .history-images button {
    position: relative;
    overflow: hidden;
    border: 0;
    padding: 0;
  }
  .history-images img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
  .history-images button span {
    position: absolute;
    left: 5px;
    bottom: 5px;
    padding: 3px 5px;
    background: rgba(27, 34, 30, 0.7);
    color: #fff;
    font-size: 7px;
  }
  .history-images > div {
    display: grid;
    place-items: center;
    background: #f4f6f5;
    color: #9aa29d;
    font-size: 8px;
  }
  .history-actions {
    display: flex;
    gap: 6px;
    padding: 8px;
  }
  .history-actions button {
    flex: 1;
    border: 1px solid #dfe5e1;
    border-radius: 4px;
    padding: 5px;
    background: #fff;
    color: #667169;
    font-size: 8px;
  }
  .history-actions .danger {
    flex: 0 0 28px;
    color: #c04c41;
  }
  button {
    cursor: pointer;
  }
  button:disabled {
    cursor: not-allowed;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (max-width: 1200px) {
    .object-replace-layout {
      grid-template-columns: 1fr;
    }
    .controls {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      overflow: visible;
    }
    .controls section {
      border: 0;
    }
    .generate-bar {
      grid-template-columns: 1fr auto auto;
    }
    .generate {
      grid-column: 1/4;
    }
    .history-list {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 780px) {
    .object-replace {
      width: calc(100% - 24px);
      margin-top: 18px;
    }
    .intro {
      flex-direction: column;
    }
    .controls {
      display: block;
    }
    .canvas-shell {
      min-height: 410px;
    }
    .generate-bar {
      display: flex;
      align-items: stretch;
      flex-direction: column;
    }
    .cost {
      align-items: flex-start;
    }
    .history-list {
      grid-template-columns: 1fr;
    }
    .stage footer > div {
      flex-wrap: wrap;
      gap: 6px 12px;
    }
  }
}

.object-replace {
  width: calc(100% - 68px);
  max-width: none;
  margin: 26px auto 64px;
  box-sizing: border-box;
  color: #243029;
}
.replacement-guide{display:grid;grid-template-columns:minmax(240px,.72fr) minmax(520px,1.7fr);gap:18px;margin-bottom:16px;border:1px solid #dbe5df;border-radius:9px;padding:17px 18px;background:linear-gradient(135deg,#f8fbf9 0%,#f4f7fd 100%)}
.replacement-guide>header{display:flex;align-items:center;gap:11px}.replacement-guide>header>span{width:38px;height:38px;flex:0 0 38px;display:grid;place-items:center;border-radius:8px;background:#2563eb;color:#fff;font-size:17px}.replacement-guide>header>div{min-width:0;display:flex;flex-direction:column;gap:4px}.replacement-guide>header strong{color:#2b3830;font-size:13px}.replacement-guide>header small{color:#77847c;font-size:9px;line-height:1.55}
.replacement-guide ol{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin:0;padding:0;list-style:none}.replacement-guide li{min-width:0;display:grid;grid-template-columns:24px minmax(0,1fr) 14px;align-items:center;gap:7px;border:1px solid #e0e7e2;border-radius:7px;padding:8px;background:rgba(255,255,255,.88)}.replacement-guide li>b{width:24px;height:24px;display:grid;place-items:center;border-radius:50%;background:#edf3ff;color:#2563eb;font-size:9px}.replacement-guide li>span{min-width:0;display:flex;flex-direction:column;gap:3px}.replacement-guide li strong{overflow:hidden;color:#435048;font-size:9px;text-overflow:ellipsis;white-space:nowrap}.replacement-guide li small{color:#8a958e;font-size:7px;line-height:1.4}.replacement-guide li>i{color:#a4aea7;font-size:10px}.replacement-guide li>i.ready{color:#27814d}
.replacement-guide>p{grid-column:1/-1;display:flex;align-items:flex-start;gap:7px;margin:-5px 0 0;border-left:3px solid #e1a635;padding:7px 10px;background:#fffaf0;color:#78643d;font-size:8px;line-height:1.55}.replacement-guide>p>i{flex:0 0 auto;margin-top:1px;color:#bc7a12}
.object-replace-layout {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(360px, 0.82fr) minmax(520px, 1.18fr);
  gap: 16px;
  margin: 0;
  box-sizing: border-box;
}
.controls,
.stage {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e2e7e3;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(37, 51, 43, 0.04);
}
.controls {
  display: flex;
  flex-direction: column;
  padding: 22px;
}
.controls section + section {
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid #edf0ee;
}
.controls :deep(.step-title) {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 14px;
}
.controls :deep(.step-title > span) {
  width: 29px;
  height: 29px;
  display: grid;
  place-items: center;
  flex: 0 0 29px;
  border-radius: 5px;
  background: #edf4ff;
  color: #2563eb;
  font-size: 10px;
  font-weight: 800;
}
.controls :deep(.step-title > div) {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.controls :deep(.step-title strong) {
  color: #2f3c34;
  font-size: 13px;
}
.controls :deep(.step-title small) {
  color: #8b948e;
  font-size: 9px;
}
.upload {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  overflow: hidden;
  border: 1px dashed #bdc8c0;
  border-radius: 6px;
  background: linear-gradient(145deg, #fbfcfb, #f7faf8);
  color: #7d8881;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.upload.source {
  height: 190px;
}
.upload.reference {
  height: 118px;
  margin-bottom: 14px;
}
.upload:hover,
.upload.dragging {
  border-color: #2563eb;
  background: #f6f9ff;
}
.upload input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.upload-icon {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  margin-bottom: 3px;
  border-radius: 50%;
  background: #edf4ff;
  color: #2563eb;
  font-size: 22px;
}
.reference .upload-icon {
  width: 38px;
  height: 38px;
  margin-bottom: 0;
  font-size: 18px;
}
.upload strong {
  color: #455149;
  font-size: 11px;
}
.upload strong em,
.description-label em {
  color: #9aa39d;
  font-size: 9px;
  font-style: normal;
  font-weight: 500;
}
.upload small {
  color: #929c96;
  font-size: 9px;
}
.upload.filled img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #f2f4f2;
}
.upload b {
  position: absolute;
  right: 7px;
  bottom: 7px;
  border-radius: 4px;
  padding: 5px 7px;
  background: rgba(22, 29, 25, 0.78);
  color: #fff;
  font-size: 8px;
}
.remove-reference {
  width: 26px;
  height: 26px;
  position: absolute;
  top: 7px;
  right: 7px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  padding: 0;
  background: rgba(22, 29, 25, 0.78);
  color: #fff;
  font-size: 16px;
}
.material-library-trigger { width: 100%; min-height: 52px; display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 12px; padding: 0 16px; border: 1px solid #b9c9ed; border-radius: 8px; background: #f3f7ff; color: #245ac8; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 5px 14px rgba(37, 99, 235, .08); transition: border-color .18s ease, background .18s ease, box-shadow .18s ease, transform .18s ease; }
.material-library-trigger i { font-size: 17px; }
.material-library-trigger:hover:not(:disabled) { border-color: #7f9fdf; background: #eaf1ff; box-shadow: 0 8px 18px rgba(37, 99, 235, .13); transform: translateY(-1px); }
.material-library-trigger:disabled { cursor: wait; opacity: .65; }
.selected-material-name { margin: 7px 0 0; overflow: hidden; color: #68766e; font-size: 10px; line-height: 1.5; text-overflow: ellipsis; white-space: nowrap; }
.selected-material-name i { margin-right: 4px; color: #2563eb; }
.material-compression-note { display: flex; align-items: flex-start; gap: 5px; margin: 6px 0 0; color: #6b7fa5; font-size: 9px; line-height: 1.5; }
.material-compression-note i { margin-top: 2px; color: #2563eb; }
.reference-cutout-trigger { width: 100%; min-height: 58px; display: grid; grid-template-columns: 36px minmax(0, 1fr); align-items: center; gap: 10px; margin-top: 10px; border: 1px solid #a9c5b1; border-radius: 8px; padding: 9px 12px; background: #f2faf4; color: #255f38; text-align: left; cursor: pointer; box-shadow: 0 5px 14px rgba(39, 116, 64, .08); transition: border-color .18s ease, background .18s ease, box-shadow .18s ease, transform .18s ease; }
.reference-cutout-trigger > i { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 50%; background: #dff1e4; color: #23743d; font-size: 17px; }
.reference-cutout-trigger > span { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.reference-cutout-trigger strong { color: #285b38; font-size: 12px; line-height: 1.35; }
.reference-cutout-trigger small { color: #66806e; font-size: 9px; line-height: 1.45; }
.reference-cutout-trigger:hover:not(:disabled) { border-color: #68a87a; background: #eaf7ed; box-shadow: 0 8px 18px rgba(39, 116, 64, .13); transform: translateY(-1px); }
.reference-cutout-trigger:focus-visible { outline: 2px solid #216d38; outline-offset: 2px; }
.reference-cutout-trigger:disabled { cursor: wait; opacity: .7; transform: none; }
.reference-cutout-trigger.completed { border-color: #60a875; background: #ebf8ef; }
.reference-cutout-error { display: flex; align-items: flex-start; gap: 6px; margin: 7px 0 0; color: #b23b3b; font-size: 10px; line-height: 1.5; }
.reference-cutout-error i { flex: 0 0 auto; margin-top: 2px; }
.operation-mode {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}
.operation-mode button {
  min-height: 76px;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  border: 1px solid #dce2de;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
  color: #66736b;
  text-align: left;
  cursor: pointer;
  transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
}
.operation-mode button:hover:not(:disabled) { border-color: #9ab7ed; background: #f7f9fd; }
.operation-mode button.active { border-color: #2563eb; background: #f1f6ff; box-shadow: inset 0 0 0 1px #2563eb; }
.operation-mode button:focus-visible { outline: 2px solid #164fae; outline-offset: 2px; }
.operation-mode button:disabled { opacity: .55; cursor: not-allowed; }
.operation-mode button > i { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 7px; background: #f0f3f1; color: #65736a; font-size: 16px; }
.operation-mode button.active > i { background: #2563eb; color: #fff; }
.operation-mode button > span { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.operation-mode button strong { color: #2c3b33; font-size: 11px; }
.operation-mode button small { color: #84918a; font-size: 8px; line-height: 1.45; }
.operation-mode button > b { align-self: start; border-radius: 9px; padding: 3px 6px; background: #eaf2ff; color: #2563eb; font-size: 8px; }
.selection-method {
  width: 100%;
  min-height: 60px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #dce2de;
  border-radius: 6px;
  padding: 11px 12px;
  background: #fafbfa;
  color: #66736b;
  text-align: left;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.selection-method:hover,
.selection-method.ready {
  border-color: #9ab7ed;
  background: #f7f9fd;
}
.selection-method > i {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  border-radius: 5px;
  background: #2563eb;
  color: #fff;
  font-size: 16px;
}
.selection-method > span {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}
.selection-method strong {
  color: #2c3b33;
  font-size: 11px;
}
.selection-method small {
  overflow: hidden;
  color: #8491a3;
  font-size: 9px;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.selection-method b {
  color: #7f8a83;
  font-size: 9px;
  white-space: nowrap;
}
.selection-method.ready b {
  color: #2563eb;
}
.selection-list {
  margin-top: 8px;
}
.selection-list button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #dfe5e1;
  border-radius: 4px;
  padding: 7px 8px;
  background: #fff;
  color: #68736d;
  font-size: 9px;
}
.selection-list button:hover:not(:disabled) {
  border-color: #9ab5e9;
  color: #2563eb;
}
.selection-list i {
  width: 17px;
  height: 17px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-size: 9px;
}
.selection-list span {
  flex: 1;
  text-align: left;
}
.selection-list b {
  color: #2563eb;
  font-size: 8px;
}
.selection-empty {
  margin: 9px 2px 0;
  color: #8e9892;
  font-size: 9px;
  line-height: 1.6;
}
.lighting-size-lock {
  margin-top: 10px;
  border: 1px solid #b9c9ed;
  border-radius: 7px;
  padding: 11px;
  background: #f5f8ff;
}
.lighting-size-lock header {
  display: flex;
  align-items: center;
  gap: 9px;
}
.lighting-size-lock header > span {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  flex: 0 0 30px;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
}
.lighting-size-lock header > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.lighting-size-lock header strong {
  color: #244f9f;
  font-size: 11px;
}
.lighting-size-lock header small {
  color: #7182a3;
  font-size: 9px;
}
.lighting-size-lock dl {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin: 10px 0;
}
.lighting-size-lock dl > div {
  min-width: 0;
  border-radius: 5px;
  padding: 7px;
  background: #fff;
}
.lighting-size-lock dt {
  color: #8b97ad;
  font-size: 8px;
}
.lighting-size-lock dd {
  overflow: hidden;
  margin: 3px 0 0;
  color: #2d4f91;
  font-size: 10px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lighting-size-lock > label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #51627f;
  font-size: 9px;
  font-weight: 700;
}
.lighting-size-lock > label + label { margin-top: 8px; }
.lighting-size-lock select {
  min-width: 0;
  height: 34px;
  flex: 1;
  border: 1px solid #c9d5ed;
  border-radius: 5px;
  padding: 0 8px;
  outline: none;
  background: #fff;
  color: #40516c;
  font: inherit;
}
.lighting-size-lock select:focus-visible {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
}
.lighting-size-lock > p {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 9px 0 0;
  color: #7182a3;
  font-size: 8px;
  line-height: 1.55;
}
.lighting-size-lock > p i {
  margin-top: 2px;
  color: #2563eb;
}
.description-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  color: #3f4c44;
  font-size: 11px;
  font-weight: 700;
}
.description-label > span {
  display: flex;
  align-items: center;
  gap: 5px;
}
.description-label small {
  color: #a2aaa5;
  font-size: 8px;
  font-weight: 400;
}
.description-tools {
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}
.description-tools > small {
  flex: 0 0 auto;
  color: #a2aaa5;
  font-size: 8px;
  font-variant-numeric: tabular-nums;
}
.description-actions {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}
.description-actions button {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #b9cbed;
  border-radius: 6px;
  padding: 0 12px;
  background: #f7faff;
  color: #2559b2;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.description-actions .description-optimizer {
  width: auto;
  margin: 0;
}
.description-actions button:hover:not(:disabled) {
  border-color: #2563eb;
  background: #edf4ff;
}
.description-actions button:disabled {
  cursor: wait;
  opacity: 0.6;
}
.description-actions button i {
  font-size: 11px;
}
.controls textarea {
  width: 100%;
  height: 78px;
  resize: none;
  box-sizing: border-box;
  border: 1px solid #dce2de;
  border-radius: 5px;
  padding: 10px 11px;
  outline: none;
  color: #435048;
  font-family: inherit;
  font-size: 11px;
  line-height: 1.6;
}
.controls textarea:focus {
  border-color: #6d96e9;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}
.controls textarea:disabled {
  background: #f7f9f8;
  color: #7e8982;
}
.quick-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 9px;
}
.quick-prompts button {
  border: 1px solid #dfe4e1;
  border-radius: 4px;
  padding: 5px 8px;
  background: #f8f9f8;
  color: #6f7a73;
  font-size: 8px;
}
.quick-prompts button:hover:not(:disabled) {
  border-color: #9ab5e9;
  background: #f5f8ff;
  color: #2563eb;
}
.stage {
  min-height: 730px;
  display: flex;
  flex-direction: column;
  background: #f5f6f5;
}
.stage-toolbar {
  min-height: 70px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e1e5e2;
  padding: 11px 14px;
  background: #fff;
}
.stage-heading {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
}
.stage-heading > span {
  width: 29px;
  height: 29px;
  display: grid;
  place-items: center;
  flex: 0 0 29px;
  border-radius: 5px;
  background: #edf4ff;
  color: #2563eb;
  font-size: 10px;
  font-weight: 800;
}
.stage-heading > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.stage-heading strong {
  color: #2f3c34;
  font-size: 12px;
  white-space: nowrap;
}
.stage-heading small {
  overflow: hidden;
  color: #8b948e;
  font-size: 8px;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.stage-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}
.toolbar-group {
  display: flex;
  align-items: center;
  gap: 3px;
  border: 1px solid #e0e4e1;
  border-radius: 5px;
  padding: 3px;
  background: #f7f8f7;
}
.toolbar-group button {
  height: 30px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  border-radius: 3px;
  padding: 0 9px;
  background: transparent;
  color: #77827b;
  font-size: 9px;
}
.toolbar-group button.active {
  background: #2563eb;
  color: #fff;
  box-shadow: 0 3px 7px rgba(37, 99, 235, 0.2);
}
.toolbar-group button:disabled {
  opacity: 0.35;
}
.toolbar-group.compact button {
  width: 31px;
  justify-content: center;
  padding: 0;
}
.image-meta {
  flex: 0 0 auto;
  color: #929b95;
  font-size: 8px;
}
.canvas-shell {
  min-height: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 18px;
  background-color: #f4f6f4;
  background-image: radial-gradient(#dfe4e0 0.8px, transparent 0.8px);
  background-size: 15px 15px;
}
.canvas-shell canvas {
  max-width: 100%;
  max-height: 590px;
  display: block;
  background: #fff;
  box-shadow: 0 12px 30px rgba(35, 45, 39, 0.16);
  cursor: crosshair;
  touch-action: none;
  user-select: none;
}
.placeholder {
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
}
.canvas-empty-art {
  width: 112px;
  height: 86px;
  position: relative;
  margin-bottom: 18px;
}
.canvas-empty-art > span {
  width: 84px;
  height: 68px;
  position: absolute;
  left: 4px;
  bottom: 0;
  display: grid;
  place-items: center;
  border: 1px solid #d6dfd9;
  border-radius: 8px;
  background: #fff;
  color: #8f9b94;
  font-size: 27px;
  box-shadow: 0 12px 30px rgba(45, 58, 50, 0.09);
}
.canvas-empty-art .art-crop {
  width: 34px;
  height: 34px;
  position: absolute;
  right: 2px;
  top: 0;
  display: grid;
  place-items: center;
  border: 3px solid #f4f6f4;
  border-radius: 7px;
  background: #2563eb;
  color: #fff;
  font-size: 15px;
  box-shadow: 0 7px 16px rgba(37, 99, 235, 0.2);
}
.canvas-empty-art .art-spark {
  position: absolute;
  top: 6px;
  left: 1px;
  color: #6e91dc;
  font-size: 14px;
  font-style: normal;
}
.placeholder strong {
  color: #354139;
  font-size: 13px;
}
.placeholder p {
  margin: 6px 0 0;
  color: #87928b;
  font-size: 9px;
}
.placeholder > em {
  margin-top: 13px;
  border-radius: 4px;
  padding: 6px 10px;
  background: #edf4ff;
  color: #4f72b8;
  font-size: 8px;
  font-style: normal;
}
.canvas-hint {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 16px;
  padding: 8px 12px;
  background: rgba(27, 34, 30, 0.82);
  color: #fff;
  font-size: 8px;
  pointer-events: none;
  white-space: nowrap;
}
.result {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.result img {
  max-width: 100%;
  max-height: 590px;
  display: block;
  object-fit: contain;
  box-shadow: 0 12px 30px rgba(35, 45, 39, 0.16);
}
.result > span {
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 14px;
  padding: 7px 10px;
  background: rgba(28, 119, 67, 0.9);
  color: #fff;
  font-size: 8px;
}
.stage-footer {
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid #e0e5e1;
  padding: 9px 14px;
  background: #fff;
  color: #7e8882;
  font-size: 9px;
}
.stage-tips {
  display: flex;
  align-items: center;
  gap: 14px;
}
.stage-tips span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.stage-tips i {
  color: #5d83cf;
}
.stage-result-actions {
  display: flex;
  align-items: center;
  gap: 7px;
}
.stage-footer button {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #b8c9eb;
  border-radius: 4px;
  padding: 6px 9px;
  background: #f5f8ff;
  color: #2459bb;
  font-size: 8px;
}
.stage-footer button.local-edit-action {
  border-color: #2563eb;
  background: #2563eb;
  color: #fff;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.18);
}
.stage-footer button:hover:not(:disabled) {
  border-color: #7598dc;
  background: #edf3ff;
}
.stage-footer button.local-edit-action:hover:not(:disabled) {
  border-color: #2563eb;
  background: #2563eb;
}
.error-card {
  display: flex;
  align-items: center;
  gap: 9px;
  border-top: 1px solid #f0c4c1;
  padding: 10px 14px;
  background: #fff2f1;
  color: #b43228;
}
.error-card > div {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}
.error-card strong {
  font-size: 9px;
}
.error-card small {
  color: #8e5b57;
  font-size: 8px;
}
.error-card button {
  border: 1px solid #d68d86;
  border-radius: 4px;
  padding: 5px 8px;
  background: #fff;
  color: #b43228;
  font-size: 8px;
}
.error-card.processing {
  border-color: #e2b85c;
  background: #fff8e6;
  color: #875a08;
}
.error-card.processing small {
  color: #765f31;
}
.error-card.processing button {
  border-color: #d8b25e;
  background: #fffdf7;
  color: #875a08;
}
.generate-bar {
  width: 100%;
  box-sizing: border-box;
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) 230px;
  align-items: center;
  gap: 22px;
  margin: 16px 0 28px;
  border: 1px solid #dce3de;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;
  box-shadow: 0 9px 24px rgba(37, 51, 43, 0.05);
}
.generate-intro {
  min-width: 185px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 20px;
  border-right: 1px solid #e7ebe8;
}
.generate-intro > span {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  border-radius: 6px;
  background: #edf4ff;
  color: #2563eb;
  font-size: 15px;
}
.generate-intro > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.generate-intro strong {
  color: #2f3c34;
  font-size: 11px;
}
.generate-intro small,
.cost small {
  color: #909992;
  font-size: 8px;
  white-space: nowrap;
}
.generation-status {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.lighting-model-settings {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}
.lighting-model-select {
  width: 210px;
  flex: 0 0 210px;
}
.lighting-model-select ::v-deep .el-input__inner {
  height: 40px;
  border-color: #d7dee8;
  border-radius: 7px;
  padding-left: 12px;
  color: #3f4f63;
  font-size: 11px;
  line-height: 40px;
}
.lighting-model-select ::v-deep .el-input__icon {
  line-height: 40px;
}
.lighting-quality-control {
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #cfe0fb;
  border-radius: 11px;
  padding: 4px 5px 4px 11px;
  background: #f6f9ff;
  box-shadow: 0 3px 12px rgba(37, 99, 235, 0.06);
}
.lighting-quality-control > span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #435a7c;
  font-size: 10px;
  white-space: nowrap;
}
.lighting-quality-control > span i {
  color: #2563eb;
  font-size: 14px;
}
.lighting-quality-control .el-select {
  width: 142px;
}
.lighting-quality-control ::v-deep .el-input__inner {
  height: 32px;
  border-color: #d3e0f6;
  border-radius: 8px;
  padding-left: 11px;
  background: #fff;
  color: #214d91;
  font-size: 11px;
  font-weight: 700;
  line-height: 32px;
}
.lighting-quality-control ::v-deep .el-input__icon {
  color: #6c89b8;
  line-height: 32px;
}
.readiness {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
}
.readiness span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #e0e5e1;
  border-radius: 5px;
  padding: 8px 9px;
  background: #f7f9f7;
  color: #8a948e;
  font-size: 9px;
  white-space: nowrap;
}
.readiness span.ready {
  border-color: #b8d8c1;
  background: #f3faf5;
  color: #287548;
}
.readiness i {
  font-size: 11px;
}
.cost {
  min-width: 94px;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
  padding-left: 18px;
  border-left: 1px solid #e7ebe8;
}
.cost strong {
  color: #59645e;
  font-size: 11px;
  white-space: nowrap;
}
.cost i {
  width: 9px;
  height: 9px;
  display: inline-block;
  border-radius: 50%;
  background: #f6a344;
  box-shadow: inset 0 0 0 2px #ffd8a3;
  vertical-align: -1px;
}
.generate {
  width: 100%;
  min-width: 230px;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 6px;
  padding: 9px 14px;
  background: #2563eb;
  color: #fff;
  box-shadow: 0 9px 20px rgba(37, 99, 235, 0.2);
  transition: background 0.18s ease, transform 0.18s ease;
}
.generate:hover:not(:disabled) {
  background: #1d4ed8;
  transform: translateY(-1px);
}
.generate:disabled {
  opacity: 0.52;
  box-shadow: none;
}
.generate > i {
  font-size: 18px;
}
.generate > span:not(.spinner) {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 2px;
}
.generate strong {
  font-size: 12px;
}
.generate small {
  color: rgba(255, 255, 255, 0.72);
  font-size: 8px;
}
.spinner {
  width: 17px;
  height: 17px;
  border: 2px solid rgba(255, 255, 255, 0.32);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.generate-progress {
  height: 3px;
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  overflow: hidden;
  border-radius: 0 0 8px 8px;
  background: #e5e9e6;
}
.generate-progress i {
  height: 100%;
  display: block;
  background: #2563eb;
  transition: width 0.3s ease;
}
.prompt-panel {
  width: 100%;
  box-sizing: border-box;
  margin: -10px 0 28px;
  overflow: hidden;
  border: 1px solid #dce3de;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 9px 24px rgba(37, 51, 43, 0.04);
}
.prompt-panel summary {
  min-height: 60px;
  display: flex;
  align-items: center;
  gap: 11px;
  box-sizing: border-box;
  padding: 12px 16px;
  cursor: pointer;
  list-style: none;
  user-select: none;
}
.prompt-panel summary::-webkit-details-marker {
  display: none;
}
.prompt-panel summary:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.35);
  outline-offset: -3px;
}
.prompt-panel-icon {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  border-radius: 6px;
  background: #edf4ff;
  color: #2563eb;
  font-size: 15px;
}
.prompt-panel-heading {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
}
.prompt-panel-heading strong {
  color: #2f3c34;
  font-size: 12px;
}
.prompt-panel-heading small {
  color: #818c85;
  font-size: 10px;
  line-height: 1.5;
}
.prompt-panel-toggle {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  border-radius: 5px;
  color: #758079;
  transition: transform 0.18s ease, background 0.18s ease;
}
.prompt-panel[open] .prompt-panel-toggle {
  transform: rotate(180deg);
  background: #f2f5f3;
}
.prompt-panel-body {
  border-top: 1px solid #e4e9e5;
  padding: 14px 16px 16px;
  background: #fbfcfb;
}
.prompt-panel-note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 10px;
}
.prompt-panel-note > span {
  color: #6f7a73;
  font-size: 10px;
  line-height: 1.5;
}
.prompt-panel-note > span i {
  margin-right: 4px;
  color: #2563eb;
}
.prompt-panel-note button {
  min-width: 118px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #b8c9eb;
  border-radius: 5px;
  background: #f5f8ff;
  color: #2459bb;
  font-size: 10px;
}
.prompt-panel-note button:hover {
  border-color: #7f9fe0;
  background: #edf4ff;
}
.prompt-panel pre {
  max-height: 260px;
  margin: 0;
  overflow: auto;
  border: 1px solid #dfe5e1;
  border-radius: 6px;
  padding: 14px;
  background: #fff;
  color: #3d4942;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
  font-size: 11px;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
  user-select: text;
}
.prompt-panel pre:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}
.history {
  width: 100%;
  box-sizing: border-box;
  margin-top: 32px;
  border-top: 1px solid #dfe5e0;
  padding-top: 26px;
}
.history > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 16px;
}
.history > header > div {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
}
.history header div > span {
  color: #2563eb;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 1.8px;
}
.history h2 {
  margin: 7px 0 4px;
  color: #202b25;
  font-size: 20px;
}
.history header small {
  color: #7a857e;
  font-size: 10px;
}
.history header button {
  width: 34px;
  height: 34px;
  border: 1px solid #dce2de;
  border-radius: 5px;
  background: #fff;
  color: #78837c;
  transition: border-color 0.18s ease, color 0.18s ease;
}
.history header button:hover {
  border-color: #9ab5e9;
  color: #2563eb;
}
.history-empty {
  min-height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px dashed #ced8d1;
  border-radius: 8px;
  padding: 40px 16px;
  background: rgba(255, 255, 255, 0.62);
  text-align: center;
}
.history-empty > span {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #edf4ff;
  color: #6d8dce;
  font-size: 20px;
  box-shadow: inset 0 0 0 1px #dce7fa;
}
.history-empty strong {
  margin-top: 12px;
  color: #536159;
  font-size: 11px;
}
.history-empty small {
  margin-top: 5px;
  color: #929c96;
  font-size: 9px;
}
.history-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}
.history-list article {
  min-width: 0;
  border: 1px solid #dce3de;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(37, 51, 43, 0.06);
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}
.history-list article:hover {
  border-color: #c8d3cb;
  box-shadow: 0 16px 36px rgba(37, 51, 43, 0.1);
  transform: translateY(-2px);
}
.history-list article.active {
  border-color: #789be3;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.07);
}
.history-list article.processing,
.history-list article.active.processing {
  border-color: #e0b95f;
  box-shadow: 0 0 0 2px rgba(217, 164, 48, 0.12);
}
.history-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
}
.history-badges {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.history-meta span {
  border-radius: 3px;
  padding: 4px 6px;
  background: #e8f7ed;
  color: #25834c;
  font-size: 8px;
}
.history-meta span.generating {
  background: #fff5e8;
  color: #c87820;
}
.history-meta span.failed {
  background: #fff0ef;
  color: #c33a30;
}
.history-meta .history-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #eef4ff;
  color: #245fc5;
  font-weight: 700;
  white-space: nowrap;
}
.history-meta small {
  min-width: 0;
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  color: #a0a8a3;
  font-size: 8px;
}
.history-meta small b {
  color: #8d9791;
  font-weight: 700;
}
.history-meta small time {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-comparison {
  height: 230px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border: 1px solid #dfe5e1;
  border-radius: 6px;
  background: #dfe5e1;
}
.history-preview {
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  position: relative;
  overflow: hidden;
  border: 0;
  border-radius: 0;
  padding: 0;
  background: #eef0ee;
  color: #7e8982;
  cursor: zoom-in;
}
.history-preview img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  object-position: center;
  background: #eef0ee;
  transition: filter 0.18s ease, transform 0.18s ease;
}
.history-preview.result img {
  background: #edf3f1;
}
.history-image-label {
  position: absolute;
  z-index: 2;
  top: 9px;
  left: 9px;
  border-radius: 4px;
  padding: 5px 7px;
  background: rgba(26, 35, 30, 0.78);
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  backdrop-filter: blur(5px);
}
.history-preview.result .history-image-label {
  background: rgba(37, 99, 235, 0.88);
}
.history-preview > i:not(.el-icon-loading):not(.el-icon-warning-outline) {
  width: 30px;
  height: 30px;
  position: absolute;
  z-index: 2;
  right: 9px;
  bottom: 9px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(20, 29, 24, 0.74);
  color: #fff;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.history-preview:hover img {
  filter: brightness(0.92);
  transform: scale(1.02);
}
.history-preview:hover > i:not(.el-icon-loading):not(.el-icon-warning-outline),
.history-preview:focus-visible
  > i:not(.el-icon-loading):not(.el-icon-warning-outline) {
  opacity: 1;
  transform: translateY(0);
}
.history-preview:focus-visible {
  z-index: 1;
  outline: 3px solid #2563eb;
  outline-offset: -3px;
}
.history-placeholder {
  cursor: default;
}
.history-placeholder > i {
  color: #8a9690;
  font-size: 22px;
}
.history-placeholder > i.el-icon-loading {
  color: #b9770e;
}
.history-placeholder.result.processing {
  border-color: #ead18d;
  background: #fff9e9;
  color: #875a08;
}
.history-placeholder.result.processing > small {
  color: #80642d;
}
.history-placeholder > i.el-icon-warning-outline {
  color: #c46a62;
}
.history-placeholder > small {
  color: #8b958f;
  font-size: 9px;
}
.history-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
}
.history-actions .workflow-tuning-button {
  flex: 1;
  width: auto;
}
.history-actions button {
  min-height: 32px;
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px solid #e0e4e1;
  border-radius: 4px;
  background: #f8f9f8;
  color: #6f7a73;
  font-size: 9px;
}
.history-actions button.danger {
  max-width: 34px;
  color: #c24136;
}
.history-actions button.local-edit-action {
  border-color: #9eb9ec;
  background: #eef4ff;
  color: #245fc5;
  font-weight: 700;
}
button {
  cursor: pointer;
}
button:disabled {
  cursor: not-allowed;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 1180px) {
  .replacement-guide { grid-template-columns: 1fr; }
  .replacement-guide > p { grid-column: auto; }
  .object-replace-layout {
    grid-template-columns: 1fr;
  }
  .stage {
    min-height: 640px;
  }
  .generate-bar {
    grid-template-columns: auto 1fr;
  }
  .generate {
    grid-column: 1 / 3;
  }
  .generation-status {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .generation-status > .readiness {
    flex: 1 1 100%;
    justify-content: flex-end;
  }
  .history-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 820px) {
  .object-replace {
    width: calc(100% - 26px);
    margin-top: 18px;
  }
  .replacement-guide { padding: 14px; }
  .replacement-guide ol { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .controls {
    padding: 18px;
  }
  .stage {
    min-height: 520px;
  }
  .stage-toolbar {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .stage-toolbar-actions {
    order: 3;
    width: 100%;
    margin-left: 0;
  }
  .image-meta {
    display: none;
  }
  .canvas-shell {
    min-height: 410px;
  }
  .stage-tips {
    flex-wrap: wrap;
    gap: 7px 12px;
  }
  .generate-bar {
    display: flex;
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }
  .generate-intro {
    min-width: 0;
    border-right: 0;
    border-bottom: 1px solid #e7ebe8;
    padding: 0 0 12px;
  }
  .generate-intro small {
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .generation-status {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
  .lighting-model-settings {
    width: 100%;
    align-items: stretch;
    flex-wrap: wrap;
  }
  .lighting-model-select {
    width: 100%;
    flex: 1 1 220px;
  }
  .lighting-model-select ::v-deep .el-input__inner,
  .lighting-model-select ::v-deep .el-input__icon {
    height: 44px;
    line-height: 44px;
  }
  .lighting-quality-control {
    min-height: 44px;
    flex: 1 1 240px;
  }
  .lighting-quality-control .el-select {
    min-width: 0;
    flex: 1;
  }
  .readiness {
    width: 100%;
    flex-wrap: wrap;
  }
  .cost {
    width: 100%;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    border-top: 1px solid #e7ebe8;
    border-left: 0;
    padding: 12px 0 0;
  }
  .generate {
    min-width: 0;
  }
  .history-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 520px) {
  .operation-mode { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .replacement-guide ol { grid-template-columns: 1fr; }
  .stage-toolbar-actions {
    justify-content: space-between;
  }
  .toolbar-group:first-child {
    flex: 1;
  }
  .toolbar-group:first-child button {
    flex: 1;
    justify-content: center;
  }
  .stage-footer {
    align-items: flex-start;
    flex-direction: column;
  }
  .stage-result-actions {
    width: 100%;
  }
  .stage-result-actions button {
    flex: 1;
  }
  .canvas-hint {
    max-width: calc(100% - 36px);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .readiness span {
    flex: 1 1 calc(50% - 7px);
    justify-content: center;
  }
  .lighting-size-lock dl { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .lighting-size-lock > label { align-items: stretch; flex-direction: column; }
  .prompt-panel summary,
  .prompt-panel-body {
    padding-right: 12px;
    padding-left: 12px;
  }
  .prompt-panel-note {
    align-items: stretch;
    flex-direction: column;
  }
  .prompt-panel-note button {
    width: 100%;
  }
  .history-list {
    grid-template-columns: 1fr;
  }
  .history-comparison {
    height: 210px;
  }
  .description-tools {
    align-items: flex-end;
    flex-direction: column;
  }
  .description-actions {
    width: 100%;
  }
  .description-actions > button,
  .description-actions .description-optimizer {
    flex: 1;
  }
}
@media (pointer: coarse) {
  .description-actions > button {
    min-height: 44px;
    font-size: 12px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .lighting-model-select ::v-deep .el-input__inner,
  .lighting-quality-control ::v-deep .el-input__inner {
    transition: none;
  }
}
</style>

<style>
.lighting-model-menu.el-select-dropdown,
.lighting-quality-menu.el-select-dropdown {
  overflow: hidden;
  border: 1px solid #d9e3f2;
  border-radius: 9px;
  box-shadow: 0 12px 30px rgba(31, 48, 76, 0.16);
}
.lighting-model-menu .el-select-dropdown__list,
.lighting-quality-menu .el-select-dropdown__list {
  padding: 5px;
}
.lighting-model-menu .el-select-dropdown__item,
.lighting-quality-menu .el-select-dropdown__item {
  height: 40px;
  margin: 2px 0;
  border-radius: 6px;
  padding: 0 11px;
  color: #45566f;
  font-size: 12px;
  line-height: 40px;
}
.lighting-model-menu .el-select-dropdown__item.hover,
.lighting-model-menu .el-select-dropdown__item:hover,
.lighting-quality-menu .el-select-dropdown__item.hover,
.lighting-quality-menu .el-select-dropdown__item:hover {
  background: #f1f6ff;
}
.lighting-model-menu .el-select-dropdown__item.selected,
.lighting-quality-menu .el-select-dropdown__item.selected {
  background: #eaf2ff;
  color: #245fc5;
  font-weight: 700;
}
</style>
